import type { PanelCopyTree, UiResources } from './types.ts';

/** UI copy, Turkish (D9). Sentences use the formal "siz", no exclamation marks (§9.7). */
export const uiTr: UiResources & { panel: PanelCopyTree } = {
  common: {
    appName: 'TalkOlog',
    language: 'Dil',
    languageNames: { en: 'English', es: 'Español', tr: 'Türkçe' },
    loading: 'Yükleniyor…',
    retry: 'Tekrar dene',
    close: 'Kapat',
    errorTitle: 'Bir sorun oluştu',
  },
  /**
   * The panel's Turkish, keyed by the paths of `apps/web/src/copy/en.ts` (the panel's English, D9):
   * the shell, sign-in and the global states (W1-03), the Reports archive and Report review with
   * the chips, source links and players they render (W1-17). `apps/web/src/copy/tr-keys.test.ts` checks
   * every key against that file. A counted English phrase (`activeSites_one` / `_other`) has one
   * Turkish key without the suffix: Turkish keeps the noun singular after a number. Buttons and
   * menu items are short imperatives ("Çıkış yap"); sentences address the reader as "siz".
   */
  panel: {
    app: {
      name: 'TalkOlog',
      tagline: 'Anlatın. Kayda geçsin.',
      skipToContent: 'İçeriğe geç',
    },
    nav: {
      overview: 'Genel bakış',
      sites: 'Şantiyeler',
      reports: 'Raporlar',
      hours: 'Puantaj',
      weather: 'Hava ve gecikmeler',
      setupLabel: 'Kurulum',
      team: 'Ekip ve numaralar',
      glossary: 'Şantiye sözlüğü',
      settings: 'Ayarlar',
      billing: 'Faturalandırma',
      try: 'WhatsApp demosu',
      devPhone: 'Demo telefon',
      badges: {
        reportsToReview: '{{count}} rapor inceleme bekliyor',
        unknownSenders: '{{count}} bilinmeyen numara',
        termsToReview: '{{count}} terim inceleme bekliyor',
      },
      openMenu: 'Menüyü aç',
      closeMenu: 'Menüyü kapat',
      mainNavigation: 'Ana gezinme',
    },
    shell: {
      orgSwitcher: {
        label: 'Şirket değiştir',
        activeSites: '{{count}} aktif şantiye',
        currentOrg: 'Geçerli şirket',
        otherOrgs: 'Şirketleriniz',
      },
      search: {
        placeholder: 'Rapor, şantiye veya taşeron arayın',
        label: 'Arama',
        clear: 'Aramayı temizle',
        noResults: '“{{query}}” için sonuç bulunamadı.',
        hint: 'En az 2 karakter yazın',
        groups: {
          report: 'Raporlar',
          site: 'Şantiyeler',
          company: 'Firmalar',
          transcript: 'Konuşma dökümleri',
          reporter: 'Rapor verenler',
          term: 'Sözlük',
        },
      },
      notifications: {
        label: 'Bildirimler',
        unread: '{{count}} yeni',
        title: 'Bildirimler',
        empty: 'Yeni bir şey yok. Raporlar ve uyarılar burada görünecek.',
        markAllRead: 'Tümünü okundu işaretle',
        notices: {
          late_upload: {
            titleAudio: '{{site}}: {{date}} için geç gelen sesli not',
            titlePhoto: '{{site}}: {{date}} için geç gelen fotoğraf',
            body: '{{reporter}} bunu o gün kaydetti; günün rapor süresi kapandıktan sonra geldi. {{reportNo}} numaralı raporda, geç olarak işaretli.',
            bodyUnnamed:
              'Şantiye şefi bunu o gün kaydetti; günün rapor süresi kapandıktan sonra geldi. {{reportNo}} numaralı raporda, geç olarak işaretli.',
          },
          missing_report: {
            title: '{{site}}: rapor yok',
            body: '{{first}} bugünün raporunu göndermedi ve son saat geçti.',
            bodyUnnamed: 'Şantiye şefi bugünün raporunu göndermedi ve son saat geçti.',
            bodyReminders: '{{first}} {{count}} hatırlatmaya yanıt vermedi.',
          },
          report_edited: { title: '{{reportNo}} onaydan sonra düzenlendi' },
          needs_review: { title: '{{reportNo}} inceleme bekliyor' },
          glossary_review: { title: 'İncelenecek {{count}} yeni terim' },
          pack_ready: {
            title: 'EOT kanıt dosyası hazır: {{site}}',
            titleAllSites: 'EOT kanıt dosyası hazır: tüm şantiyeler',
            body: '{{from}} – {{to}}',
          },
          processing_delayed: { title: '{{reportNo}} her zamankinden uzun sürüyor' },
          processing_failed: {
            title: '{{reportNo}}: işleme durdu',
            body: 'TalkOlog yaklaşık üç saat içinde {{tries}} kez denedi ve durdu ({{job}}). Rapor veren taslağını bekliyor olabilir. TalkOlog ekibi uyarıldı; kaynaklarını kontrol etmek için raporu açın.',
          },
          message_not_sent: {
            title: '{{name}}: ona giden bir mesaj gönderilemedi',
            body: 'TalkOlog yaklaşık üç saat içinde {{tries}} kez denedi ve durdu ({{job}}). Bir yanıt bekliyor olabilir; bugün önemliyse kendisiyle doğrudan iletişime geçin. TalkOlog ekibi uyarıldı.',
          },
          message_not_handled: {
            title: '{{name}}: ondan gelen bir mesaj işlenemedi',
            body: 'TalkOlog yaklaşık üç saat içinde {{tries}} kez denedi ve durdu ({{job}}). Bir yanıt bekliyor olabilir; bugün önemliyse kendisiyle doğrudan iletişime geçin. TalkOlog ekibi uyarıldı.',
          },
          message_opted_out: {
            title: '{{name}}: ona giden bir mesaj gönderilemedi',
            body: 'Numarası SMS sağlayıcısının çıkış listesinde; START yazana kadar TalkOlog ona mesaj göndermez.',
          },
          message_refused: {
            title: '{{name}}: ona giden bir mesaj gönderilemedi',
            body: 'SMS sağlayıcısı numarasını reddetti ({{error}}). Numarayı Ekip ve numaralar sayfasında kontrol edin.',
          },
          message_channels_failed: {
            title: '{{name}}: ona giden bir mesaj gönderilemedi',
            body: 'TalkOlog onun için kullanabildiği tüm mesaj kanallarını denedi ({{channels}}) ve hiçbiri mesajı iletemedi ({{error}}). Numarasını ve kanallarını Ekip ve numaralar sayfasında kontrol edin.',
          },
          message_account_fault: {
            title: '{{name}}: ona giden bir mesaj SMS sağlayıcısında başarısız oldu',
            titleUnnamed: 'Bir mesaj SMS sağlayıcısında başarısız oldu',
            body: 'Bu hesap için mesajlar başarısız oluyor ({{error}}). SMS sağlayıcısı mesajı kabul etti, sonra rapor verenin numarası yüzünden değil, hesap ya da gönderici yüzünden reddetti. TalkOlog varsa diğer mesaj kanallarını dener. Önümüzdeki bir saat içinde aynı şekilde başarısız olan mesajlar için yeniden bildirim gelmez.',
          },
          message_no_sender: {
            title: '{{name}}: ona giden bir mesaj gönderilemedi',
            body: 'Bu ortamda şirketin ülkesi için SMS veya WhatsApp göndericisi tanımlı değil, bu yüzden mesaj hiçbir telefona ulaşmadı.',
          },
          welcome_undeliverable: { title: '{{name}}: karşılama mesajı iletilemedi' },
          welcome_provider_fault: {
            title: '{{name}}: karşılama mesajı SMS sağlayıcısında başarısız oldu',
            body: 'Bu hesap için mesajlar başarısız oluyor ({{error}}). TalkOlog ekibi uyarıldı; rapor veren davet edildi olarak işaretlenmedi. Sağlayıcı düzelince karşılama mesajını Ekip ve numaralar sayfasından yeniden gönderin.',
          },
          welcome_not_sent: {
            title: '{{name}}: karşılama mesajı gönderilmedi',
            bodies: {
              opted_out:
                'Mesajlardan çıktı; yeniden kabul edene kadar (yanıt alan paketlerde START) TalkOlog ona mesaj göndermez.',
              no_consent:
                'Onay sorulduğunda mesajları reddetti, bu yüzden TalkOlog ona SMS veya WhatsApp ile mesaj göndermez.',
              no_channel:
                'Ülkesinin hiçbir mesaj kanalı karşılama mesajını ve linkini taşıyamıyor. Kendisine başka bir yoldan ulaşın.',
              console_real_org:
                'Bu ülkeye mesajlar bu ortamda henüz canlı değil: ülke paketi için SMS veya WhatsApp göndericisi tanımlı değil, bu yüzden rapor verene bir şey ulaşmadı. Bir gönderici tanımlanınca karşılama mesajını Ekip ve numaralar sayfasından yeniden gönderin.',
            },
          },
          welcome_carrier_undelivered: {
            title: '{{name}}: karşılama mesajı iletilemedi',
            body: 'Operatör karşılama mesajını iletemedi ({{error}}): numara sabit hat, engelli ya da kullanım dışı olabilir. Ekip ve numaralar sayfasında kontrol edin ya da kendisine başka bir yoldan ulaşın.',
          },
          welcome_account_fault: {
            title: '{{name}}: karşılama mesajı SMS sağlayıcısında başarısız oldu',
            titleUnnamed: 'Bir karşılama mesajı SMS sağlayıcısında başarısız oldu',
            body: 'Bu hesap için mesajlar başarısız oluyor ({{error}}). SMS sağlayıcısı karşılama mesajını kabul etti, sonra rapor verenin numarası yüzünden değil, hesap ya da gönderici yüzünden reddetti. Sağlayıcı hesabı düzelince karşılama mesajını Ekip ve numaralar sayfasından yeniden gönderin. Önümüzdeki bir saat içinde aynı şekilde başarısız olan mesajlar için yeniden bildirim gelmez.',
          },
          reporter_opted_out: {
            title: '{{name}} mesajlardan çıktı',
            body: '{{phone}} SMS sağlayıcısının çıkış listesinde (bu göndericiye daha önce STOP yazdı); START yazana kadar TalkOlog ona mesaj göndermez. Raporlarına ihtiyacınız varsa kendisini arayın.',
          },
          reporter_stop: {
            title: '{{name}} mesajlardan çıktı (STOP)',
            body: '{{phone}} STOP yazdı; START yazana kadar TalkOlog ona mesaj göndermez. Raporlarına ihtiyacınız varsa kendisini arayın.',
          },
          unknown_contact: { title: 'Bilinmeyen bir numara rapor göndermeye çalıştı' },
          reporter_consent_revoked: {
            titles: {
              recording: '{{name}} raporlarının kaydedilmesine verdiği onayı kapattı',
              data_processing: '{{name}} raporlarının işlenmesine verdiği onayı kapattı',
            },
            body: 'Onayı telefonundaki Ayarlar ekranından kapattı; telefonundan yeniden onay verene kadar TalkOlog ondan rapor almaz. Beklenmedik bir durumsa kendisiyle iletişime geçin.',
          },
          device_verify_locked: {
            title: '{{name}} için telefon doğrulaması bir saatliğine kilitlendi',
            body: 'Bir saat içinde linkine 5 kez yanlış kod girildi; TalkOlog bir saat boyunca yeni kod kabul etmiyor. Bunu yapan kendisi değilse linki başkasının elinde olabilir: kendisiyle iletişime geçin.',
          },
          reporter_wrong_site: {
            title: '{{name}} şantiyenin yanlış olduğunu söylüyor',
            body: 'Ekip sayfasında şantiye atamasını kontrol edin.',
          },
          payment_failed: {
            title: 'Ödeme başarısız',
            body: 'Stripe kartınızdan ödeme alamadı. Raporların ve hatırlatmaların sürmesi için Faturalandırma sayfasında ödeme yöntemini güncelleyin.',
          },
          subscription_canceled: {
            title: 'Abonelik iptal edildi',
            body: 'Abonelik Faturalandırma sayfasından yeniden başlatılana kadar hatırlatmalar ve yeni şantiye günleri durur.',
          },
          late_report: {
            title: '{{site}}: geç rapor geldi',
            body: '{{reportNo}} · sahada {{crew}} kişi · {{hours}}',
          },
          invoice_gap: {
            title: 'Fatura farkı: {{company}} {{sign}}{{gap}}',
            body: '{{invoiceNo}} faturası {{invoiced}} · raporlar {{reported}} gösteriyor',
          },
        },
      },
      footer: {
        agentReady: 'Asistan hazır · mesaj ve arama',
        sitelogNumbers: 'TalkOlog numaraları',
        profile: 'Profil',
        signOut: 'Çıkış yap',
        userMenu: 'Hesap menüsü',
      },
      demo: {
        chip: 'Demo',
        chipTitle: 'Demo modu: bellekteki örnek veriler, hiçbir şey kaydedilmez',
        menuTitle: 'Demo kontrolleri',
        simulate: 'Rapor simüle et',
        simulateHint: 'Elm St. geç bir rapor gönderir',
        simulated: 'Priya’nın raporu geldi. Elm St. raporu geç kaydedildi.',
        alreadyFiled: 'Elm St. bugünkü raporunu zaten gönderdi.',
        resetHint: 'Verileri sıfırlamak için çıkış yapıp yeniden giriş yapın',
        switchUser: 'Demo kullanıcısını değiştir',
        users: {
          admin: 'Lena Ortiz · yönetici',
          pm: 'Sam Kowalski · proje müdürü',
          ops: 'Ravi Patel · operasyon',
          accounts: 'Muhasebe · bordro',
        },
      },
      offline: 'Çevrimdışısınız. Bağlantı gelince değişiklikler yeniden gönderilecek.',
      backOnline: 'Yeniden çevrimiçisiniz.',
    },
    common: {
      loading: 'Yükleniyor…',
      cancel: 'Vazgeç',
      confirm: 'Onayla',
      close: 'Kapat',
      total: 'Toplam',
      hoursUnit: 'sa',
      filter: 'Filtre',
      showMore: 'Daha fazla göster',
      required: 'zorunlu',
      moreActions: 'Diğer işlemler',
      // W4-02: the setup wizard.
      back: 'Geri',
      optional: 'isteğe bağlı',
    },
    table: {
      empty: 'Henüz gösterilecek bir şey yok.',
    },
    /** §11.3 ChannelIcon (W1-16). */
    channel: {
      link: 'Link',
      call: 'Arama',
      office: 'Yazılı',
      reported: {
        link: 'Mesajdaki linkle raporlandı',
        call: 'Telefonla raporlandı',
        office: 'Panelde yazıldı',
      },
      prefers: {
        link: 'Mesajdaki linkle rapor verir',
        call: 'Telefonla rapor verir',
        office: 'Panelde yazıldı',
      },
    },
    /** P3 Overview (W1-16). */
    overview: {
      title: 'Bugün {{count}} şantiyede',
      pdfs: 'Bugünün PDF’leri',
      pdfsDemo: 'Demo modu: PDF’ler her dosyalanan rapordan arka planda oluşturulur.',
      pdfsNone: 'Bugün henüz PDF yok.',
      kpisLabel: 'Rakamlarla bugün',
      kpis: {
        reportsIn: 'Gelen raporlar',
        crew: 'Sahadaki ekip',
        crewUnit: 'çalışan',
        hours: 'Kaydedilen işçilik saati',
        weather: 'Hava etkisindeki şantiyeler',
        weatherLink: 'Gecikme kaydına eklendi',
        segments: {
          filed: 'Dosyalandı',
          weather: 'Hava engeli',
          missing: 'Eksik',
          awaiting: 'Bekleniyor',
        },
      },
      sitesToday: 'Bugün şantiyeler',
      allSites: 'Tüm şantiyeler',
      columns: {
        site: 'Şantiye',
        status: 'Durum',
        channel: 'Kanal',
        inAt: 'Geliş',
        crew: 'Ekip',
        hours: 'Saat',
        weather: 'Hava',
      },
      weatherNone: 'Kuru, etkisi yok',
      weatherFrom: '{{time}} itibarıyla',
      lostHours: '{{hours}} kayıp',
      remindersSent: '{{count}} hatırlatma gönderildi',
      needsAttention: 'İlgilenmeniz gerekenler',
      allClear: 'Her şey yolunda',
      allClearBody: 'Tüm şantiyeler raporladı, sizi bekleyen bir şey yok.',
      nothingYet: 'Şimdilik sizi bekleyen bir şey yok',
      nothingYetBody: 'Bugün {{count}} şantiyenin raporu bekleniyor.',
      emptyBeforeReports:
        'Raporlar genellikle {{deadline}} sonrasında gelir. Hatırlatma saati: {{r1}}.',
      emptyBeforeReportsNoReminders: 'Raporlar genellikle {{deadline}} sonrasında gelir.',
      emptyNoReporter: 'Henüz rapor veren şantiye şefi yok',
      emptyNoReporterBody:
        'Bir şantiye şefi mesajla link ya da telefonla rapor vermeye davet edilene kadar günün raporunu panelden yazın.',
      noSitesTitle: 'Henüz şantiye yok',
      noSites:
        'İlk şantiyenizi bağlayın, ardından günlük raporlarını Raporlar → Yeni rapor üzerinden dosyalayın.',
      noSitesAskAdmin:
        'Şantiyeleri yöneticiler bağlar. İlk şantiyeyi bağlaması için bir yöneticiye danışın.',
      connectFirstSite: 'İlk şantiyenizi bağlayın',
      allPausedTitle: 'Tüm şantiyeler duraklatıldı',
      allPaused: 'Bir şantiyeyi ayarlarından sürdürün; günlük raporlar yeniden başlar.',
      allPausedAskAdmin:
        'Şantiyeleri yöneticiler sürdürür. Birini sürdürmesi için bir yöneticiye danışın; günlük raporlar yeniden başlar.',
      openSites: 'Şantiyeleri aç',
      demoPhoneCard: {
        eyebrow: 'Demo',
        title: 'Tüm mesajlar Demo telefona düşer',
        body: 'Bu bir demo şirket: karşılama mesajları, günün linkleri ve hatırlatmalar gerçek telefonlara değil Demo telefona gider. Oradaki bir linki açıp şantiye şefi olarak rapor verin, sonra bu sayfaya dönüp güncellenmesini izleyin.',
        cta: 'Demo telefonu aç',
      },
      attention: {
        labels: {
          missing: 'Eksik',
          needs_review: 'İnceleme gerekli',
          invoice_gap: 'Fatura kontrolü',
          unknown_contact: 'Bilinmeyen kişi',
          glossary_review: 'Sözlük',
          undeliverable: 'İletilemedi',
        },
        missing: {
          title: '{{site}}: rapor yok',
          body: '{{first}} {{count}} hatırlatmaya yanıt vermedi.',
          bodyNoReminders: '{{first}} son saate kadar rapor göndermedi.',
          alerted: 'Ofise bildirildi: {{time}}.',
          call: 'Ara: {{first}}',
          textLink: 'Günün linkini gönder',
          markNoWork: 'İş yok olarak işaretle',
        },
        needs_review: {
          title: '{{site}} inceleme bekliyor',
          titleUnknownCompany: '{{site}}: bilinmeyen taşeron “{{name}}”',
          unknownCompany: '{{count}} çalışan anıldı, taşeron listenizde yok.',
          review: 'Raporu incele',
        },
        reasons: {
          unknown_company: 'Bilinmeyen taşeron “{{name}}”',
          low_confidence: 'Düşük güven: {{field}}',
          unanswered: 'Yanıtlanmamış soru: {{field}}',
          unconfirmed: 'Şantiye şefi onaylamadan dosyalandı',
          missing: 'Eksik: {{field}}',
          no_photo: 'Aramadan sonra fotoğraf gelmedi',
          call_mismatch: 'Arama kaydı onaylanan rapordan farklı: {{field}}',
          call_unreconciled: 'Arama kaydı raporla karşılaştırılamadı',
          other: '{{reason}}',
        },
        invoice_gap: {
          title: '{{company}}: {{invoiced}} faturalandı',
          bodyOver: 'Bu haftanın günlük raporları {{reported}} gösteriyor; fark {{gap}}.',
          bodyUnder:
            'Bu haftanın günlük raporları {{reported}} gösteriyor; faturadan {{gap}} fazla.',
          compare: 'Saatleri karşılaştır',
        },
        unknown_contact: {
          titleCall: 'Bilinmeyen arayan',
          titleText: 'Bilinmeyen numaradan mesaj',
          titleWhatsapp: 'Bilinmeyen numara rapor göndermeye çalıştı',
          body: '{{phone}} · “{{snippet}}”',
          bodyNoSnippet: '{{phone}}',
          link: 'Numarayı bağla',
        },
        glossary_review: {
          title: '{{count}} sözlük terimi inceleme bekliyor',
          body: '{{terms}}',
          review: 'Terimleri incele',
        },
        undeliverable: {
          title: '{{name}}: mesaj iletilemedi',
          body: 'Gönderilen mesaj (saat {{time}}) telefonuna ulaşmadı.',
          call: 'Ara: {{first}}',
          checkNumber: 'Numarayı kontrol et',
        },
      },
      textLink: {
        sent: 'Günün linki gönderildi: {{first}}.',
        notSent: {
          no_consent: '{{first}} mesaj almayı kabul etmedi, bu yüzden bir şey gönderilmedi.',
          opted_out: '{{first}} mesajlardan çıktı, bu yüzden bir şey gönderilmedi.',
          no_channel: '{{first}} için uygun bir mesaj kanalı yok, bu yüzden bir şey gönderilmedi.',
          console_real_org:
            'Bu şirketin ülkesi için mesajlar henüz canlı değil, bu yüzden {{first}} bir şey almadı.',
        },
        failed: {
          no_reporter:
            'Bu şantiyeye bağlı bir şantiye şefi yok. Ekip ve numaralar sayfasından ekleyin.',
          no_active_reporter:
            'Bu şantiyede aktif şantiye şefi yok: bağlı olanlar devre dışı, mesajlardan çıkmış ya da davetini henüz kabul etmemiş.',
          reporter_not_linked: 'Bu kişi artık o şantiyeye bağlı değil.',
          reporter_not_active:
            'Bu kişi henüz aktif değil: devre dışı, mesajlardan çıkmış ya da davetini henüz kabul etmemiş.',
        },
      },
      markNoWorkDialog: {
        title: '{{site}} için iş yok olarak işaretlensin mi?',
        body: '{{date}} iş yapılmayan gün olarak kaydedilecek. Şantiye şefi yine de geç rapor gönderebilir; o zaman bu kaydın yerini alır.',
        confirm: 'İş yok olarak işaretle',
        done: '{{site}} iş yok olarak işaretlendi.',
      },
      liveUpdates: 'Canlı güncellemeler açık',
    },
    /** §11.3 status chips (every page's `StatusChip`). */
    chips: {
      filed: 'Dosyalandı',
      awaiting: 'Bekleniyor',
      missing: 'Eksik',
      needs_review: 'İnceleme gerekli',
      weather_day: 'Hava engeli',
      no_work: 'İş yok',
      signed_off: 'Onaylandı',
      pending: 'Bekleniyor',
      late: 'Geç',
      lateAtTime: 'Geç {{time}}',
      paused: 'Duraklatıldı',
      active: 'Aktif',
      archived: 'Arşivlendi',
      unconfirmed: 'Doğrulanmadı',
      confirmed: 'Doğrulandı',
      confirmedByReply: 'Yanıtla doğrulandı',
      inviteSent: 'Davet gönderildi',
      optedOut: 'Mesajları reddetti',
      disabled: 'Devre dışı',
      open: 'Açık',
      queried: 'Soruldu',
      resolved: 'Çözüldü',
      paid: 'Ödendi',
      pastDue: 'Vadesi geçti',
      ready: 'Hazır',
      building: 'Hazırlanıyor',
      queued: 'Sırada',
      failed: 'Başarısız',
      autoLearned: 'Otomatik öğrenildi',
      builtIn: 'Yerleşik',
      rejected: 'Reddedildi',
    },
    /** §11.3 SourceLink, MediaPlayer, ChannelIcon (W1-17). */
    source: {
      links: {
        audio: 'ses {{ts}}',
        call: 'arama {{ts}}',
        answer: 'yanıt {{time}}',
        reply: 'yanıt {{time}}',
        editedBy: '{{name}} düzenledi',
        editedByOffice: 'ofis düzenledi',
        reporterEdit: 'telefonda değiştirildi {{time}}',
        weather: 'hava verisi',
        playVoiceNote: 'Sesli notu {{ts}} noktasından çal',
        playCall: 'Aramayı {{ts}} noktasından çal',
        showAnswer: 'Yanıtı Etkinlik bölümünde göster',
        playAnswer: 'Sesli yanıtı çal ve Etkinlik bölümünde göster',
        openHistory: 'Sürüm geçmişini aç',
      },
    },
    player: {
      play: 'Sesli notu çal',
      pause: 'Sesli notu duraklat',
      playCall: 'Arama kaydını çal',
      pauseCall: 'Arama kaydını duraklat',
      label: 'Sesli not',
      labelCall: 'Arama kaydı',
      languages: '{{from}} → {{to}}',
      noAudio: 'Ses kaydı yok',
      demoAudio: 'Demo oynatma (sessiz)',
      demoTones: 'Demo kaydı: ses tonları, konuşma yok',
      speed: 'Oynatma hızı',
      speedValue: '{{rate}}×',
      laneReporter: 'Şantiye şefi',
      laneAgent: 'TalkOlog asistanı',
      speakerFilter: 'Konuşan',
      filter: {
        both: 'İkisi de',
        reporter: 'Şantiye şefi',
        agent: 'Asistan',
      },
      answerClip: 'Sesli yanıt',
    },
    errors: {
      generic: 'Bir sorun oluştu. Tekrar deneyin.',
    },
    toasts: {
      signedOffReport: '{{no}} onaylandı.',
      undone: 'Onay geri alındı.',
      versionSavedN: 'Sürüm {{version}} kaydedildi.',
      companyResolved: 'Firma çözüldü. Yeni sürüm kaydedildi.',
      invoiceAdded: 'Fatura eklendi.',
      termReviewed: 'Sözlük güncellendi.',
      termAdded: 'Terim eklendi.',
      packRequested: 'Kanıt dosyası istendi.',
      // W4-10: Billing's and Site settings' Pause / Resume.
      sitePausedName: '{{site}} duraklatıldı.',
      siteResumedName: '{{site}} yeniden başlatıldı.',
      // W4-02: the setup wizard's company step.
      orgCreated: '{{name}} oluşturuldu.',
      // W4-05: Settings' saves.
      saved: 'Kaydedildi.',
      settingsSaved: 'Ayarlar kaydedildi.',
      logoUpdated: 'Logo güncellendi.',
      logoRemoved: 'Logo kaldırıldı.',
      // W4-09: the save toasts of Sites, Crew hours and the company details.
      orgUpdated: 'Şirket bilgileri kaydedildi.',
      siteCreated: '{{site}} bağlandı.',
      siteUpdated: 'Şantiye güncellendi.',
      csvReady: 'Bordro CSV hazır.',
    },
    // W4-10: P13 Billing (spec §19 Q234–Q236), for the native-speaker read (Q63).
    billing: {
      title: 'Faturalandırma',
      subtitle: 'Aktif şantiye başına aylık faturalandırılır. Kurulum ücreti yoktur.',
      subtitlePrice: 'Aktif şantiye başına aylık {{price}}. Kurulum ücreti yoktur.',
      plan: 'Mevcut plan',
      perMonth: '/ ay',
      equation: '{{count}} × {{price}} = {{total}}',
      noPrice: 'Şantiye başına fiyatınızı TalkOlog belirler. Burada henüz görünmüyorsa bize sorun.',
      nextInvoice: 'Sonraki fatura',
      paymentMethod: 'Ödeme yöntemi',
      updatePayment: 'Ödeme yöntemini güncelle',
      whatCounts: 'Aktif sayılan nedir',
      whatCountsActive: 'Rapor alabildiği sürece aktiftir.',
      whatCountsPaused:
        'Duraklatılan şantiyelerde hatırlatmalar ve faturalandırma bir sonraki dönemden itibaren durur.',
      whatCountsProrated: 'Eklenen şantiyeler Stripe tarafından kıst olarak faturalandırılır.',
      whatCountsManual: 'Eklenen şantiyeler TalkOlog’un bir sonraki faturasına yansır.',
      sitesOnPlan: 'Bu plandaki şantiyeler',
      thing: 'faturalandırma',
      columns: {
        site: 'Şantiye',
        since: 'Aktif olduğu tarih',
        status: 'Durum',
        monthly: 'Aylık',
        action: 'İşlem',
      },
      pause: 'Duraklat',
      resume: 'Devam ettir',
      pauseSiteLabel: '{{site}} şantiyesini duraklat',
      resumeSiteLabel: '{{site}} şantiyesini devam ettir',
      pauseDialog: {
        title: '{{site}} duraklatılsın mı?',
        body: 'Hatırlatmalar durur ve bir sonraki dönemden itibaren faturalandırılmaz.',
        confirm: 'Şantiyeyi duraklat',
      },
      resumeDialog: {
        title: '{{site}} devam ettirilsin mi?',
        body: 'Hatırlatmalar yeniden başlar ve şantiye bugünden itibaren faturalandırılır.',
        confirm: 'Şantiyeyi devam ettir',
      },
      activeSitesLabel: 'Aktif şantiyeler',
      card: 'Sonu {{last4}} ile biten {{brand}}',
      noPaymentMethod: 'Kayıtlı ödeme yöntemi yok',
      trial: 'Ücretsiz deneme',
      trialEnds: 'Deneme {{date}} tarihinde bitiyor',
      trialEnded: 'Deneme {{date}} tarihinde bitti',
      noSubscription: 'Henüz abonelik yok',
      status: 'Faturalandırma durumu',
      statuses: {
        trialing: 'Ücretsiz deneme',
        active: 'Aktif',
        past_due: 'Ödemesi gecikmiş',
        canceled: 'İptal edildi',
        paused: 'Duraklatıldı',
      },
      portalDemo: 'Demo modu: canlı ortamda Stripe müşteri portalı burada açılır.',
      portalOpening: 'Stripe müşteri portalı açılıyor…',
      checkoutDemo: 'Demo modu: canlı ortamda Stripe ödeme sayfası burada açılır.',
      checkoutOpening: 'Stripe ödeme sayfası açılıyor…',
      startTitle: 'Aboneliğinizi başlatın',
      startBody:
        '14 günlük ücretsiz deneme. Raporların ve hatırlatmaların sürmesi için 15. günden önce kart ekleyin; faturalandırma aktif şantiye başınadır.',
      startCta: 'Ödeme bilgilerini ekle',
      startNoSites:
        'Ödeme bilgilerini eklemeden önce bir şantiye ekleyin. Faturalandırma aktif şantiye başınadır.',
      startResumeSite:
        'Ödeme bilgilerini eklemeden önce aşağıdan bir şantiyeyi devam ettirin ya da yeni bir şantiye ekleyin. Faturalandırma aktif şantiye başınadır.',
      addSite: 'Şantiye ekle',
      noSitesTitle: 'Planda şantiye yok',
      noSitesBody: 'Her aktif şantiye aylık faturalandırılır.',
      noSitesBodyPrice: 'Her aktif şantiye için ayda {{price}} faturalandırılır.',
      checkoutSuccess: 'Abonelik başladı. Burada görünmesi bir dakika sürebilir.',
      checkoutCanceled: 'Ödeme iptal edildi. Abonelik başlatılmadı.',
      pausedSince: '{{date}} tarihinden beri',
      invoicesEmpty: 'Henüz fatura yok.',
      invoiceDownload: '{{date}} faturasını indir',
      notBilled: '—',
      invoices: 'Faturalar',
      invoiceColumns: { date: 'Tarih', sites: 'Şantiye', amount: 'Tutar', status: 'Durum' },
      download: 'İndir',
      manual: {
        title: 'TalkOlog sizi doğrudan faturalandırır',
        body: 'Burada kart gerekmez. TalkOlog her ay aşağıdaki aktif şantiyeler için faturanızı gönderir; duraklatılan bir şantiye bir sonraki dönemden itibaren faturalandırılmaz.',
        invoices: 'Faturalarınız TalkOlog’dan e-postayla gelir.',
      },
      banner: {
        label: 'Faturalandırma',
        past_due:
          'Ödemeniz gecikti. Hatırlatmaların ve aramaların sürmesi için ödeme yöntemini {{date}} tarihine kadar güncelleyin.',
        pastDueNoDate:
          'Ödemeniz gecikti. Hatırlatmaların ve aramaların sürmesi için ödeme yöntemini güncelleyin.',
        trial_ended:
          'Ücretsiz denemeniz {{ended}} tarihinde bitti. Hatırlatmaların ve aramaların sürmesi için ödeme bilgilerini {{date}} tarihine kadar ekleyin.',
        manualPastDue:
          'TalkOlog faturanızın ödemesi gecikti. Ödenmezse hatırlatmalar ve aramalar {{date}} tarihinde durur.',
        blocked:
          'Hatırlatmalar ve aramalar durduruldu: ödeme gecikti. Yeniden başlatmak için ödeme yöntemini güncelleyin.',
        manualBlocked:
          'Hatırlatmalar ve aramalar durduruldu: TalkOlog faturanızın ödemesi gecikti. Yeniden başlatmak için TalkOlog ile iletişime geçin.',
        canceled:
          'Aboneliğiniz iptal edildi. Faturalandırma sayfasından yeniden başlatılana kadar hatırlatmalar ve aramalar kapalıdır.',
        action: 'Faturalandırmayı aç',
      },
    },
    // W4-02: P2 Setup wizard (F2), for the native-speaker read (Q63).
    setup: {
      title: 'Şirketinizi kurun',
      header: '{{company}} · kurulum',
      saveLater: 'Kaydet, sonra bitir',
      steps: {
        company: 'Şirket',
        site: 'İlk şantiye ve sorumlusu',
        reports: 'Raporlar ve hatırlatmalar',
      },
      stepOf: 'Adım {{n}} / {{total}}',
      stepState: { done: 'Tamamlandı', current: 'Şu anki adım', upcoming: 'Sıradaki' },
      addSiteTitle: 'Şantiye ekle',
      addSiteSubtitle:
        'Her şantiye her gün mesajdaki linkle ya da aramayla rapor verir. Kurulum ücreti yok.',
      addSiteSubtitlePrice:
        'Her şantiye her gün mesajdaki linkle ya da aramayla rapor verir. Aylık {{price}}, kurulum ücreti yok.',
      company: {
        title: 'Şirketiniz',
        subtitle: 'PDF’lerde ve karşılama mesajında görünen bilgiler.',
        name: 'Şirket adı',
        namePlaceholder: 'ör. Demir Yapı A.Ş.',
        nameRequired: 'Şirketinizin adını girin.',
        address: 'Adres',
        addressPlaceholder: 'Cadde, ilçe, il',
        licenseNo: 'Vergi / ruhsat numarası',
        logo: 'Logo',
        logoHint: 'En fazla 2 MB PNG veya SVG · PDF’lerde kullanılır',
        logoTooLarge: 'Logo en fazla 2 MB olmalı.',
        logoWrongType: 'PNG veya SVG dosyası kullanın.',
        upload: 'Logo yükle',
        replace: 'Logoyu değiştir',
        removeLogo: 'Logoyu kaldır',
        logoUploadFailed:
          'Şirket oluşturuldu ama logo yüklenemedi. Ayarlar → PDF şablonu bölümünden ekleyin.',
        logoSaveFailed: 'Logo yüklenemedi. Ayarlar → PDF şablonu bölümünden ekleyin.',
        timezone: 'Şirketin saat dilimi',
        next: 'İlk şantiyeye geç',
        country: 'Ülke',
        countryPlaceholder: 'Bir ülke seçin',
        countryHint:
          'Rapor dilini, birimleri, kâğıt boyutunu, para birimini ve saat dilimini belirler.',
        countryLocked: 'Ülke kurulumdan sonra değiştirilemez.',
        countryRequired: 'Şirketinizin ülkesini seçin.',
        countryList: 'ülke listesi',
        noCountries: 'Bu TalkOlog veritabanında henüz hiçbir ülke tanımlı değil.',
        presets: {
          title: '{{country}} için ayarlar',
          reportLanguage: 'Rapor dili',
          units: 'Birimler',
          paper: 'Kâğıt',
          currency: 'Para birimi',
        },
        unitNames: { imperial: 'İngiliz (imperial)', metric: 'Metrik' },
        paperNames: { letter: 'Letter', a4: 'A4' },
        errors: {
          unknownCountry: 'Bu ülke kullanılamıyor. Listeden bir ülke seçin.',
        },
      },
      site: {
        sectionSite: 'Şantiye',
        sectionManager: 'Şantiye sorumlusu',
        name: 'Şantiye adı',
        namePlaceholder: 'ör. Kartal Konutları — B Blok',
        nameRequired: 'Şantiye adını girin.',
        projectCode: 'Proje numarası',
        projectCodePlaceholder: 'ör. KK-1300',
        projectCodeRequired: 'Proje numarasını girin.',
        projectCodeTaken: 'Bu proje numarası zaten kullanılıyor.',
        address: 'Şantiye adresi',
        addressPlaceholder: 'Cadde, ilçe, il',
        addressHint:
          'Her rapor ve gecikme kaydı için yerel hava durumu verisini almakta kullanılır.',
        addressRequired: 'Adresi girin ya da koordinatları belirleyin.',
        geocoded: 'Konum bulundu · {{lat}}, {{lng}}',
        geocoding: 'Adres aranıyor…',
        notGeocoded:
          'Bu adres bulunamadı. Koordinatları girin; konum belirlenene kadar hava durumu verisi alınamaz.',
        manualCoords: 'Koordinatları elle gir',
        lat: 'Enlem',
        lng: 'Boylam',
        coordsInvalid: 'Enlem −90…90, boylam −180…180 arasında olmalı.',
        timezone: 'Şantiyenin saat dilimi',
        deadline: 'Rapor son saati',
        deadlineHint: 'Yerel saat · hatırlatmalar 15 dakika sonra başlar',
        managerName: 'Ad',
        managerNamePlaceholder: 'Ad soyad',
        managerNameRequired: 'Şantiye sorumlusunun adını girin.',
        phone: 'Cep telefonu',
        languages: 'Konuştuğu diller',
        backup: 'Yedek raporcu',
        backupHint:
          'İsteğe bağlı · aynı karşılama mesajını alır ve sorumlunun yerine rapor verebilir.',
        addBackup: 'Yedek raporcu ekle',
        removeBackup: 'Yedeği kaldır',
        backupName: 'Yedeğin adı',
        backupPhone: 'Yedeğin cep telefonu',
        backupLanguage: 'TalkOlog’un yedekle kullanacağı dil',
        backupPreference: 'Yedeğin tercih ettiği kanal',
        addAnother: 'Başka şantiye ekle',
        send: 'Karşılama mesajını gönder',
        sent: 'Şantiye eklendi. {{name}} için karşılama mesajı sıraya alındı.',
        inviteFailed:
          'Şantiye oluşturuldu ama {{name}} için karşılama mesajı sıraya alınamadı ({{reason}}). Ekip ve numaralar sayfasından davet edin.',
        recipientsFailed:
          'Şantiye oluşturuldu ama rapor alıcıları kaydedilemedi. Ayarlar → Alıcılar bölümünden belirleyin.',
        another: 'Şimdiye kadar {{count}} şantiye eklendi',
        lineCheck: {
          landline_text:
            'Bu bir sabit hat numarası, mesaj alamaz. Arama’yı seçin ya da bir cep telefonu numarası girin.',
          landline_unreachable:
            'Bu bir sabit hat numarası. TalkOlog bu numaraya mesaj gönderemez; Şimdi konuş için de mesajla gelen kişisel link ve kod gerekir. Bir cep telefonu numarası girin.',
        },
        preview: {
          title: 'Önizleme · {{name}} ne alacak',
          from: 'TalkOlog · kısa mesaj',
          sampleLink: 'Link örnektir. Herkes kendi linkini alır.',
          callAnyTime: 'İstediği zaman {{number}} numarasını da arayabilir.',
          unavailable: 'Önizleme yok: bu ülkede mesajlar link içeremez.',
          placeholderName: 'şantiye sorumlunuz',
          placeholderSite: 'şantiye',
        },
        pricing: {
          perSiteMonth: '/ şantiye / ay',
          setupBilled: '{{zero}} kurulum · aktif şantiye başına faturalanır',
          pointPause: 'Herhangi bir şantiyeyi istediğiniz zaman iptal edin ya da duraklatın',
          pointInvoice: 'İlk fatura gelecek ayın başında',
          sitesTimes: '{{count}} aktif şantiye × {{price}}',
        },
      },
      reports: {
        title: 'Raporlar ve hatırlatmalar',
        subtitle: 'Tüm şantiyeler için varsayılanlar. Sonra Ayarlar’dan değiştirebilirsiniz.',
        recipients: 'Rapor alıcıları',
        recipientsHint:
          'Dosyalanan PDF’ler bu adreslere gider. Şantiye başına listeleri sonra belirleyebilirsiniz.',
        addEmail: 'E-posta ekle',
        emailPlaceholder: 'ad@sirket.com',
        removeEmail: '{{email}} adresini kaldır',
        emailInvalid: 'Geçerli bir e-posta girin.',
        noRecipients: 'Henüz alıcı yok.',
        finish: 'Bitir',
        finished: 'Her şey hazır. Raporlar son saatten sonra gelir.',
      },
      resume: 'İlerleme kaydedildi. Kaldığınız yerden devam edin.',
      savedLater: 'İlerleme kaydedildi.',
      savedLaterSignedOut: 'İlerleme kaydedildi. Kurulumu bitirmek için yeniden giriş yapın.',
      pageName: 'şirket kurulumu',
    },
    reports: {
      title: 'Raporlar',
      subtitle: 'Her günlük rapor, söylenen her kelimeye kadar aranabilir',
      exportZip: 'PDF’leri dışa aktar (.zip)',
      newReport: 'Yeni rapor',
      filters: {
        site: 'Şantiye',
        allSites: 'Tüm şantiyeler',
        dates: 'Tarihler',
        from: 'Başlangıç',
        to: 'Bitiş',
        status: 'Durum',
        anyStatus: 'Tüm durumlar',
        channel: 'Kanal',
        weatherDelay: 'Hava gecikmesi var',
        edited: 'Düzenlendi',
        late: 'Geç',
        incident: 'Olay',
        search: 'Rapor metninde ve konuşma dökümlerinde arayın',
        clear: 'Filtreleri temizle',
      },
      channels: {
        any: 'Tümü',
        link: 'Link',
        call: 'Arama',
      },
      columns: {
        date: 'Tarih',
        reportNo: 'Rapor no.',
        site: 'Şantiye',
        crew: 'Ekip',
        hours: 'Saat',
        flags: 'İşaretler',
        status: 'Durum',
        pdf: 'PDF',
      },
      flags: {
        rain: 'Yağmur {{hours}}',
        version: 'v{{version}}',
        unknownSub: 'Bilinmeyen taşeron',
        late: 'Geç {{time}}',
        incident: 'Olay',
        weatherDay: 'Hava engeli',
        noPhoto: 'Fotoğraf yok',
      },
      downloadPdf: '{{no}} PDF’ini indir',
      selectRow: '{{no}} seç',
      selectAll: 'Bu sayfadakilerin tümünü seç',
      count: '{{count}} rapor',
      countSelected: '{{count}} rapor · {{selected}} seçili',
      buildPack: 'Bunlardan EOT kanıt dosyası hazırla',
      buildPackHint: 'Bu listede hava nedeniyle {{hours}} kayıp',
      empty: 'Bu filtrelere uyan rapor yok.',
      emptyHint: 'Tarih aralığını genişletin ya da bir filtreyi kapatın.',
      emptyNone: 'Henüz rapor yok',
      emptyNoneBody: 'Bir şantiyenin günlük raporunu yazın; PDF’iyle birlikte buraya kaydedilir.',
      emptyNoSitesBody: 'İlk şantiyenizi ekleyin, sonra günlük raporlarını buraya kaydedin.',
      emptyNoSitesAskAdmin:
        'Şantiyeleri bir yönetici ekler. İlk şantiyeyi eklemesini isteyin, sonra günlük raporlarını buraya kaydedin.',
      connectFirstSite: 'İlk şantiyenizi bağlayın',
      exportDemo:
        'Demo modu: burada {{count}} PDF içeren bir zip inerdi. PDF’leri çalışan (worker) hazırlar.',
      exportNone: 'Dışa aktarılacak rapor yok. Filtreleri genişletin ya da satır seçin.',
      statuses: {
        all: 'Tüm durumlar',
        filed: 'Dosyalandı',
        needs_review: 'İnceleme gerekli',
        signed_off: 'Onaylandı',
        weather_day: 'Hava engeli',
      },
      filterBar: 'Filtreler',
      toggles: 'Hızlı filtreler',
      manager: '{{name}}',
      pdfNotReady: 'PDF henüz hazır değil',
      pdfNotStored: 'Henüz kayıtlı PDF yok. Açtığınızda hazırlanır.',
      pagination: 'Sayfalar',
      showing: '{{from}}–{{to}} / {{total}}',
      page: 'Sayfa {{page}} / {{total}}',
      previousPage: 'Önceki sayfa',
      nextPage: 'Sonraki sayfa',
      thing: 'raporlar',
    },
    // W4-09: New report (/reports/new), for the native-speaker read (Q63).
    newReport: {
      title: 'Yeni rapor',
      subtitle: 'Bir şantiyenin günlük raporunu yazın. Kaydettiğiniz anda dosyalanır.',
      breadcrumb: 'Raporlar',
      site: 'Şantiye',
      pickSite: 'Şantiye seçin',
      date: 'Tarih',
      noSites: 'Rapor yazmadan önce bir şantiye ekleyin.',
      future: 'Gelecekteki bir tarih seçilemez.',
      empty: 'Dosyalamadan önce bir özet yazın veya en az bir satır ekleyin.',
      submit: 'Raporu dosyala',
      cancel: 'Vazgeç',
      filed: '{{no}} dosyalandı.',
      exists:
        'Bu şantiye ve tarih için {{no}} zaten var, bu yüzden bu rapor dosyalanmadı. Yazdıklarınız duruyor: {{no}} raporunu açıp Düzenle ile ekleyin ya da başka bir şantiye veya tarih seçin.',
      openExisting: '{{no}} raporunu aç',
      restored:
        'Bu sekmede yazdığınız rapor geri getirildi. Fotoğraflar saklanmaz: yeniden ekleyin.',
      startOver: 'Baştan başla',
      pdfLater: 'Rapor dosyalandı. PDF henüz oluşturulamadı; raporu açıp PDF düğmesine basın.',
    },
    // W4-09: Today's PDFs / Export PDFs (.zip) on the Overview and Reports.
    pdfZip: {
      preparing: 'PDF’ler hazırlanıyor…',
      progress: 'PDF’ler indiriliyor · {{done}} / {{total}}',
      making: 'PDF’ler oluşturuluyor · {{done}} / {{total}}',
      ready: '{{count}} PDF zip dosyasına eklendi.',
      partial:
        '{{count}} PDF zip dosyasına eklendi · {{skipped}} tanesi hazır değildi ya da indirilemedi.',
      none: 'Bu raporların hiçbirinin henüz PDF’i yok.',
      failed: 'Zip dosyası oluşturulamadı. Tekrar deneyin.',
      capped:
        '{{total}} raporun ilk {{limit}} tanesi dışa aktarılıyor. Geri kalanı için filtreleri daraltın.',
      labels: { today: 'günlük-raporlar', archive: 'raporlar' },
    },
    report: {
      breadcrumb: 'Raporlar',
      eyebrow: 'Günlük rapor · {{no}}',
      version: 'v{{version}}',
      versionCurrent: 'v{{version}} · güncel',
      versionSwitcher: 'Sürüm',
      previous: 'Önceki rapor',
      next: 'Sonraki rapor',
      thing: 'rapor',
      provenance: {
        reportedBy: 'Rapor veren: {{name}}',
        viaLink: 'mesajdaki linkle sesli not, {{time}}',
        viaCall: 'telefon araması {{time}} ({{duration}})',
        viaCallNoLength: 'telefon araması {{time}}',
        viaCallOut: 'TalkOlog araması {{time}} ({{duration}})',
        viaCallOutNoLength: 'TalkOlog araması {{time}}',
        viaBrowserCall: 'tarayıcı araması {{time}} ({{duration}})',
        viaBrowserCallNoLength: 'tarayıcı araması {{time}}',
        fallbackRecording: 'basit kayıt modu',
        viaText: 'mesajla, {{time}}',
        viaWhatsApp: 'Rapor veren: {{name}} · WhatsApp sesli not, {{time}}',
        confirmed: 'onaylandı {{time}}',
        confirmedOnRecorder: 'kayıt ekranında onaylandı {{time}}',
        confirmedByVoice: 'sesle onaylandı',
        confirmedByText: 'mesajla onaylandı {{time}}',
        edited: 'düzenlendi {{time}}',
        editedOnCall: 'aramada düzenlendi {{time}}',
        editedByReply: 'yanıtla düzenlendi {{time}}',
        editedBy: '{{name}} düzenledi {{time}}',
        signedOff: '{{name}} onayladı {{time}}',
        filedWithoutConfirmation: 'onaysız dosyalandı',
        enteredBy: '{{name}} TalkOlog’a girdi, {{time}}',
      },
      actions: {
        edit: 'Düzenle',
        pdf: 'PDF',
        signOff: 'Onayla',
        signedOffBy: '{{name}} onayladı',
        undo: 'Geri al',
        save: 'Yeni sürüm olarak kaydet',
        cancel: 'Vazgeç',
      },
      signOffDialog: {
        title: '{{no}} v{{version}} onaylansın mı?',
        body: 'Onay, adınız ve saatle denetim kaydına yazılır. 10 dakika içinde geri alabilirsiniz.',
        confirm: 'Onayla',
        cancel: 'Vazgeç',
      },
      sections: {
        summary: 'Özet',
        weather: 'Hava ve etkisi',
        manpower: 'İşçilik',
        work: 'Yapılan işler',
        deliveries: 'Teslimatlar',
        safety: 'İş güvenliği',
        nextWorkday: 'Sonraki iş günü',
        photos: 'Fotoğraflar',
        issues: 'Sorunlar',
        equipment: 'Ekipman',
      },
      weather: {
        observed: 'Gözlenen',
        precip: 'Yağış',
        temp: 'Sıcaklık',
        wind: 'Rüzgâr',
        gusts: 'Hamle',
        impact: 'İşe etkisi',
        attribution: 'Hava verisi: Open-Meteo',
        none: 'Hava kaynaklı etki bildirilmedi.',
        fullDay: 'Tam gün',
      },
      manpower: {
        company: 'Firma',
        tradeArea: 'İş kolu · alan',
        workers: 'İşçi',
        hours: 'Saat',
        source: 'Kaynak',
        total: 'Toplam',
        addRow: 'Firma ekle',
        removeRow: 'Satırı kaldır',
        addNewCompany: 'Yeni ekle…',
        hoursPerWorker: 'sa / işçi',
        editorTotal: 'Toplam: {{workers}} işçi · {{hours}}',
      },
      work: {
        area: 'Alan',
        activity: 'İş',
        status: 'Durum',
        heardAs: 'duyulan: “{{words}}”',
      },
      workStatus: {
        complete: 'Tamamlandı',
        in_progress: 'Devam ediyor',
        started: 'Başladı',
        stopped: 'Durdu',
        planned: 'Planlandı',
      },
      safety: {
        toolboxTalk: 'İSG konuşması: {{topic}}',
        noIncidents: 'Olay yok',
        incidents: '{{count}} olay',
        notStated: 'Belirtilmedi',
        visitors: 'Ziyaretçiler: {{names}}',
      },
      facts: {
        workers: '{{count}} işçi',
        lost: '{{hours}} kayıp',
        toolboxTalk: 'İSG konuşması: {{topic}}',
        noIncidents: 'Olay yok',
        notStated: 'Belirtilmedi',
      },
      aside: {
        source: 'Kaynak',
        voiceNote: 'Sesli not',
        voiceNotes: '{{count}} sesli not',
        call: 'Arama',
        recordings: '{{count}} kayıt',
        typed: 'TalkOlog’da yazıldı',
        typedBy: 'Bu raporu {{name}} TalkOlog’da yazdı; sesli not yok.',
        fullTranscript: 'Konuşma dökümünün tamamı',
        activity: 'Etkinlik',
        excerpts: 'Döküm alıntıları',
        clipVoiceNote: 'Sesli not · {{time}}',
        clipCall: 'Arama · {{time}}',
        noRecording: 'Bu raporla gelen kayıt yok.',
        noExcerptsForSpeaker: 'Bu konuşandan alıntı satırı yok.',
      },
      edit: {
        changeNote: 'Değişiklik notu',
        changeNotePlaceholder: 'Neyin neden değiştiği (denetim kaydına ve e-postaya girer)',
        changeNoteRequired: 'Değişiklik notu gerekli.',
      },
      diff: { edited: 'Düzenlendi v{{version}}', was: 'önceki: {{value}}' },
      questionAsked: 'Soruldu {{time}}',
      confirmedByReply: 'Yanıtla doğrulandı',
      confirmedByAnswer: 'Yanıtla doğrulandı',
      photoTaken: 'Çekildi {{when}}',
      photoAlt: 'Şantiye fotoğrafı, çekildi {{when}}',
      photoAltNoTime: 'Şantiye fotoğrafı',
      title: '{{weekday}} — {{site}}',
      versionLine: '{{author}} · {{time}}',
      versionLineNote: '{{author}} · {{time}} · {{note}}',
      versionAuthor: { agent: 'TalkOlog asistanı', reporter: 'Yanıt', office_user: 'Ofis' },
      viewingOld: 'Sürüm {{version}} görüntüleniyor. Güncel sürüm {{current}}.',
      viewCurrent: 'Güncel sürümü göster',
      signOffHidden: 'Onay, yöneticiler ve proje müdürleri içindir.',
      undoWindow: '10 dakika içinde geri alın',
      pdfPreparing: 'PDF hazırlanıyor…',
      pdfFailed: 'PDF hazırlanamadı. Tekrar deneyin.',
      pdfNotSaved: 'PDF açıldı ama henüz TalkOlog’a kaydedilemedi.',
      weatherDayNote: 'Hava nedeniyle çalışılmadı — iş yapılmadı.',
      summaryEmpty: 'Özet yok.',
      manpowerEmpty: 'İşçilik bildirilmedi.',
      workEmpty: 'Yapılan iş bildirilmedi.',
      deliveriesEmpty: 'Teslimat yok.',
      nextEmpty: 'Sonraki iş günü için bir şey belirtilmedi.',
      issuesEmpty: 'Sorun yok.',
      photosEmpty: 'Bu raporla fotoğraf yok.',
      photoPreview: 'Demo modunda önizleme yok',
      conditions: 'Koşullar',
      lostHours: '{{hours}} kayıp',
      inDelayLog: 'Gecikme kaydında · {{hours}}',
      periodRange: '{{start}}–{{end}}',
      periodFrom: '{{start}} itibarıyla',
      periodUntil: '{{end}} saatine kadar',
      quantity: '{{value}} {{unit}}',
      percent: '%{{value}}',
      manpowerSummary: '{{workers}} · {{companies}} · {{hours}}',
      workers: '{{count}} işçi',
      companies: '{{count}} firma',
      hoursUnknown: 'saat bilinmiyor',
      unconfirmedHint: 'Firma henüz doğrulanmadı — yukarıdaki uyarıdan çözün.',
      reasons: {
        title: 'İncelenecek {{count}} neden',
        unknown_company: 'Bilinmeyen taşeron “{{name}}”',
        low_confidence: 'Düşük güven: {{path}}',
        unanswered: 'Yanıtlanmayan soru: {{field}}',
        unconfirmed: 'Şantiye şefi onaylamadan dosyalandı',
        call_mismatch: 'Arama kaydı ile onaylanan rapor {{path}} konusunda farklı — kontrol edin',
        call_mismatch_workers:
          'Arama kaydı {{heard}} işçi diyor, onaylanan rapor {{confirmed}} — kontrol edin',
        call_mismatch_hours:
          'Arama kaydı işçi başına {{heard}} sa diyor, onaylanan rapor {{confirmed}} — kontrol edin',
        call_mismatch_lostHours:
          'Arama kaydı {{heard}} sa kayıp diyor, onaylanan rapor {{confirmed}} — kontrol edin',
        call_mismatch_value:
          'Arama kaydı {{path}} için {{heard}} diyor, onaylanan rapor {{confirmed}} — kontrol edin',
        call_mismatch_of: '{{what}}: {{line}}',
        no_photo: 'Bu rapor için fotoğraf gelmedi',
        call_unreconciled:
          'Arama kaydı raporla karşılaştırılamadı; saniyeleri canlı zaman damgalarıdır.',
        playAt: '{{ts}} noktasından çal',
        addAsSub: 'Taşeron olarak ekle',
        mapTo: 'Mevcut firmayla eşleştir',
      },
      activity: {
        empty: 'Henüz etkinlik yok.',
        agent: 'TalkOlog',
        office: 'Ofis',
        system: 'Sistem',
        events: {
          link_opened: 'Link açıldı',
          clip_received: 'Sesli not alındı · {{duration}}',
          clip_received_photos: 'Sesli not + {{count}} fotoğraf alındı',
          photos_received: '{{count}} fotoğraf alındı',
          report_started: 'Rapor başladı',
          extracted: 'Döküm çıkarıldı, alanlar dolduruldu',
          question_asked: 'Soru soruldu',
          question_answered: 'Soru yanıtlandı',
          question_timeout: 'Soru zamanında yanıtlanmadı',
          draft_shown: 'Taslak gösterildi',
          draft_corrected: 'Taslak düzeltildi',
          confirmed: 'Onaylandı',
          confirmed_web: 'Kayıt ekranında onaylandı',
          confirmed_voice: 'Sesle onaylandı',
          confirmed_sms: 'Mesajla onaylandı',
          filed: 'v{{version}} dosyalandı',
          version: 'v{{version}} kaydedildi',
          pdf_rendered: 'PDF hazırlandı',
          pdf_emailed: 'PDF e-postayla gönderildi',
          text_sent: 'Mesaj gönderildi · {{message}}',
          text_received: 'Mesaj alındı',
          call_in: 'Gelen arama · {{duration}}',
          call_out: 'Giden arama · {{duration}}',
          call_missed: 'Arama yanıtlanmadı',
          call_web: 'Tarayıcı araması · {{duration}}',
          whatsapp_in: 'WhatsApp gelen · {{type}}',
          whatsapp_out: 'WhatsApp giden · {{type}}',
          signed_off: 'Onaylandı',
          signoff_undone: 'Onay geri alındı',
          signoff_reverted: 'Düzenleme onayı kaldırdı',
          needs_review: 'İncelemeye alındı',
          company_resolved: 'Bilinmeyen firma çözüldü',
          no_work: 'Çalışma yok olarak işaretlendi',
          missing: 'Eksik — ofise bildirildi',
          voided: 'Rapor iptal edildi',
          other: '{{action}}',
        },
        callOutcomes: {
          filed: 'dosyalandı',
          draft_saved: 'taslak kaydedildi',
          no_work: 'çalışma yok',
          weather_day: 'hava engeli',
          edit_applied: 'düzeltme uygulandı',
          callback_requested: 'geri arama istendi',
          fallback_recording: 'basit kayıt',
          abandoned: 'rapor olmadan bitti',
          not_reached: 'ulaşılamadı',
        },
        status: {
          queued: 'sırada',
          sent: 'gönderildi',
          delivered: 'teslim edildi',
          read: 'okundu',
          received: 'alındı',
          failed: 'başarısız',
          undeliverable: 'teslim edilemedi',
        },
        messages: {
          welcome: 'Karşılama',
          verify_code: 'Cihaz kodu',
          daily_link: 'Günlük link',
          call_coming: 'Arama geliyor',
          weather_headsup: 'Hava uyarısı',
          reminder_1: 'Hatırlatma',
          reminder_2: 'İkinci hatırlatma',
          missed_call: 'Cevapsız arama',
          question_ready: 'Soru',
          draft_ready: 'Taslak hazır',
          filed: 'PDF linkiyle dosyalandı',
          photos_link: 'Fotoğraf linki',
          edit_applied: 'Düzenleme uygulandı',
          finish_report: 'Raporu bitirin',
          site_paused: 'Şantiye duraklatıldı',
          unknown_number: 'Bilinmeyen numara',
          help: 'Yardım',
          stop_confirm: 'Ret onaylandı',
          pref_call_ok: 'Arama tercih ediyor',
          pref_text_ok: 'Mesaj tercih ediyor',
          other: 'Mesaj',
        },
        whatsappTypes: {
          text: 'metin',
          audio: 'sesli not',
          image: 'fotoğraf',
          document: 'belge',
          interactive: 'yanıt',
          template: 'şablon',
          reaction: 'tepki',
          unsupported: 'mesaj',
        },
      },
      transcript: {
        title: 'Konuşma dökümünün tamamı',
        language: 'Dil: {{language}}',
        none: 'Bu rapor için konuşma dökümü yok.',
        segments: '{{count}} bölüm',
        reporter: 'Rapor veren',
        agent: 'TalkOlog',
      },
      duration: {
        seconds: '{{s}} sn',
        minutes: '{{m}} dk',
        minutesSeconds: '{{m}} dk {{s}} sn',
      },
      editMode: {
        title: 'Düzenleniyor',
        hint: 'Düzenlemeler yeni bir sürüm oluşturur. Asıl sürüm geçmişte kalır ve yeni bir PDF gönderilir.',
        versionChanged: 'Bu rapor değişti. En son sürümü görmek için sayfayı yenileyin.',
        summary: 'Özet',
        conditions: 'Koşullar',
        weatherPeriods: 'Hava dönemleri',
        addPeriod: 'Dönem ekle',
        removePeriod: 'Dönemi kaldır',
        condition: 'Durum',
        start: 'Başlangıç',
        end: 'Bitiş',
        impact: 'Etkisi',
        lostHours: 'Kayıp saat',
        company: 'Firma',
        newCompanyName: 'Yeni firma adı',
        trade: 'İş kolu',
        area: 'Alan',
        workers: 'İşçi',
        hoursPerWorker: 'Saat / işçi',
        addRow: 'Firma ekle',
        removeRow: '{{name}} satırını kaldır',
        removeRowUnnamed: 'Satırı kaldır',
        workItems: 'Yapılan işler',
        addWork: 'İş ekle',
        removeWork: 'İşi kaldır',
        activity: 'İş',
        status: 'Durum',
        percent: '% tamamlandı',
        deliveries: 'Teslimatlar',
        addDelivery: 'Teslimat ekle',
        removeDelivery: 'Teslimatı kaldır',
        item: 'Kalem',
        quantity: 'Miktar',
        supplier: 'Tedarikçi',
        time: 'Saat',
        safety: 'İş güvenliği',
        toolboxTalk: 'İSG konuşması konusu',
        noIncidents: 'Şantiye şefi olay olmadığını söyledi',
        incidents: 'Olaylar',
        addIncident: 'Olay ekle',
        removeIncident: 'Olayı kaldır',
        description: 'Açıklama',
        severity: 'Ciddiyet',
        severities: {
          near_miss: 'Ramak kala',
          first_aid: 'İlk yardım',
          recordable: 'Kayda değer',
          lost_time: 'İş günü kayıplı',
        },
        nextWorkday: 'Sonraki iş günü',
        addNext: 'Kalem ekle',
        removeNext: 'Kalemi kaldır',
        required: 'Gerekli.',
        number: 'Bir sayı girin.',
        timeInvalid: 'SS:DD biçimini kullanın (24 saat).',
        companyRequired: 'Bir firma seçin ya da yeni bir ad yazın.',
        noChanges: 'Değişiklik yok.',
        saveTitle: '{{no}} yeni sürüm olarak kaydedilsin mi?',
        saveBody: 'Bu, sürüm {{version}} olur. Önceki sürüm geçmişte kalır.',
        cancelTitle: 'Değişiklikler silinsin mi?',
        cancelBody: 'Düzenlemeleriniz kaybolur.',
        discard: 'Sil',
        keepEditing: 'Düzenlemeye devam et',
        photos: {
          title: 'Fotoğraflar',
          hint: 'Telefonunuzdan ya da bilgisayarınızdan fotoğraflar. Kaydettiğinizde küçültülüp yüklenir.',
          pdfHint: 'PDF ilk {{count}} fotoğrafı basar; görünmesi gerekenleri başa taşıyın.',
          inPdf: 'PDF’te',
          moveEarlier: '{{n}}. fotoğrafı öne al',
          moveLater: '{{n}}. fotoğrafı geriye al',
          add: 'Fotoğraf ekle',
          count: '{{n}} / {{max}}',
          countLabel: '{{max}} fotoğraftan {{count}}',
          full: 'Bir rapor en fazla {{max}} fotoğraf alır. Başka bir tane eklemek için birini kaldırın.',
          empty: 'Henüz fotoğraf yok.',
          alt: '{{n}}. fotoğraf',
          caption: '{{n}}. fotoğrafın açıklaması',
          captionPlaceholder: 'Açıklama (isteğe bağlı)',
          remove: '{{n}}. fotoğrafı kaldır',
          preparing: 'Hazırlanıyor…',
          noPreview: 'Önizleme yok',
          taken: 'Çekildi {{when}}',
          dismiss: 'Kapat',
          notImage: '{{name}} bir görsel değil.',
          tooLarge: '{{name}} 25 MB’tan büyük.',
          tooManyPixels:
            '{{name}} {{megapixels}} megapiksellik bir fotoğraf, burada hazırlanamayacak kadar büyük. 50 megapikselin altında bir kopyasını kullanın.',
          heic: '{{name}} bu tarayıcının açamadığı bir HEIC fotoğrafı. Safari kullanın ya da önce JPEG olarak kaydedin.',
          unreadable: '{{name}} açılamadı. JPEG ya da PNG deneyin.',
          limit: 'Bir rapor en fazla {{max}} fotoğraf alır; {{count}} fotoğraf eklenmedi.',
          notReady: 'Bir fotoğraf hazırlanamadı. Fotoğraflar bölümüne bakıp yeniden kaydedin.',
          uploadingProgress: 'Fotoğraflar yükleniyor: {{i}} / {{n}}',
          uploadFailed: 'Bir fotoğraf yüklenemedi. Hiçbir şey kaydedilmedi; tekrar deneyin.',
        },
      },
    },
    /** P10 Ekip ve numaralar (W4-03). */
    team: {
      title: 'Ekip ve numaralar',
      subtitle:
        'TalkOlog her aramayı ve mesajı telefon numarasından bir şantiyeyle eşleştirir. Kişisel linkler tek bir kişiye aittir.',
      thing: 'ekip',
      inviteReporter: 'Raporcu davet et',
      unknown: {
        title: 'Bilinmeyen bir numara rapor vermeye çalıştı',
        body: '{{phone}} · {{time}}',
        link: '{{site}} ile eşleştir',
        linkOther: 'Bir şantiyeyle eşleştir…',
        ignore: 'Yok say',
        snippet: '“{{text}}”',
        guessed: '{{site}} anılıyor',
      },
      link: {
        title: '{{phone}} numarasını bir şantiyeyle eşleştir',
        description: 'Bekleyen mesaj, numara eşleştirilir eşleştirilmez işlenir.',
        descriptionConsent:
          'TalkOlog ona linkiyle birlikte bir karşılama mesajı gönderir. Mesajları ve kaydı kabul ettiğinde rapor verebilir.',
        name: 'Ad',
        namePlaceholder: 'Bu kim?',
        role: 'Görev',
        site: 'Şantiye',
        sitePlaceholder: 'Bir şantiye seçin',
        confirm: 'Numarayı eşleştir',
        nameRequired: 'Bir ad girin.',
        siteRequired: 'Bir şantiye seçin.',
      },
      reportsCount: '30 günde {{count}} rapor',
      prefers: {
        link: 'Mesajla link',
        call: 'Arama',
        withChannel: '{{pref}} · {{channel}}',
      },
      channels: {
        sms: 'SMS',
        whatsapp: 'WhatsApp',
        push: 'Bildirimler',
      },
      channelNotAgreed: '{{channel}} henüz kabul edilmedi',
      locales: {
        'en-US': 'İngilizce',
        'es-US': 'İspanyolca',
        'tr-TR': 'Türkçe',
      },
      speaks: 'Konuştuğu diller: {{languages}}',
      primary: 'Birincil',
      noSites: 'Şantiye yok',
      empty: 'Henüz raporcu yok. İlk şantiye şefini davet edin.',
      status: {
        active: 'Aktif',
        invite_sent: 'Davet gönderildi',
        opted_out: 'Mesajları durdurdu',
        undeliverable: 'Bu numaraya SMS gönderilemiyor',
        disabled: 'Devre dışı',
      },
      statusNotes: {
        byPerson: 'Kendisi durdurdu (STOP)',
        byProvider: 'Operatör durdurdu',
        autoResent: 'Otomatik olarak yeniden gönderildi',
        devices: '{{count}} telefonda oturum açık',
      },
      edit: {
        title: '{{name}} kişisini düzenle',
        description: 'Değişiklikler bir sonraki mesaj ya da aramada geçerli olur.',
        phone: 'Cep telefonu',
        phoneHint:
          'Numara değiştirilemez. Yeni bir numara için bu raporcuyu devre dışı bırakın ve yeni numarayı davet edin.',
        preference: 'Tercih',
        messageChannel: 'Mesajların gideceği kanal',
        channelDefault: 'Ülkenizin sırası ({{order}})',
        language: 'TalkOlog’un kullanacağı dil',
        languageHint: 'Mesajlar, kayıt ekranı ve aramalar bu dilde olur.',
        attest: 'Bu değişikliği {{first}} istedi',
        attestHint: 'Adınızla birlikte kayda geçer.',
        attestRequired:
          'Kutuyu işaretleyin: TalkOlog’un {{first}} kişisine ulaşma şeklini yalnızca kendisi istediğinde değiştirin.',
        channelNotAgreed:
          '{{first}} henüz {{channel}} kanalını kabul etmedi. Kayıt ekranından açana kadar mesajlar başka yoldan gider.',
        save: 'Değişiklikleri kaydet',
      },
      officeEmpty: 'Ofis kullanıcısı yok.',
      changeRole: 'Rolü değiştir',
      removeUser: 'Kaldır',
      you: 'siz',
      disableDialog: {
        title: '{{name}} devre dışı bırakılsın mı?',
        body: 'TalkOlog ona mesaj göndermeyi ve aramayı bırakır, linki de çalışmaz. Yeniden etkinleştirmek için karşılama mesajını tekrar gönderin.',
      },
      removeDialog: {
        title: '{{name}} kaldırılsın mı?',
        body: 'Bu şirkete erişimini hemen kaybeder. Daha sonra yeniden davet edebilirsiniz.',
        confirm: 'Kullanıcıyı kaldır',
      },
      revokeDialog: {
        title: '{{name}} için cihazlar iptal edilsin mi?',
        body: '{{first}} kişisinin linkini açan her telefonda oturum kapanır. Bir sonraki açılışta {{phone}} numarasına gönderilen bir kod istenir. Onayları ve kişisel linki aynen kalır.',
        count: 'Şu an oturum açık: {{count}} telefon.',
        confirm: 'Cihazları iptal et',
      },
      optOutDialog: {
        title: '{{name}} için mesajlar yeniden açılsın mı?',
        body: 'Operatör bu numaraya mesajları durdurdu. Bunu yalnızca {{first}} yeniden mesaj almak istediyse yapın. Operatör yine de reddedebilir.',
        attest: '{{first}} yeniden mesaj almak istedi',
        confirm: 'Mesajları yeniden aç',
      },
      callNow: {
        title: '{{name}} şimdi aransın mı?',
        body: 'TalkOlog asistanı {{first}} kişisini {{site}} için bugünkü rapor hakkında şimdi arar. {{first}} önce görüşmenin kaydedildiğini duyar.',
        attest: '{{first}}, günlük rapor için TalkOlog’un aramasını kabul etti',
        attestHint:
          'Adınızla birlikte kayda geçer. Yalnızca bu aramaya izin verir: hatırlatma aramaları için {{first}} kişisinin kayıt uygulamasında kendi onayı gerekir.',
        noText:
          '{{first}} açmazsa TalkOlog mesaj göndermez. Aramanın nasıl geçtiğini burada görürsünüz.',
        confirm: 'Şimdi ara',
        refused: {
          attestation_required: 'Önce kutuyu işaretleyin: kişi aranmayı kabul etmiş olmalı.',
          no_trunk: 'TalkOlog şirketiniz için henüz arama yapamıyor.',
          reporter_inactive:
            'Bu raporcu devre dışı ya da mesajları durdurmuş. TalkOlog onu aramaz.',
          calls_turned_off:
            'Otomatik aramaları kayıt uygulamasında kendisi kapattı. Yalnızca kendisi yeniden açabilir.',
          no_calls_today: 'Bugün aranmak istemediğini bildirdi.',
          site_not_active: 'Şantiye duraklatılmış ya da arşivlenmiş.',
          outside_window: 'TalkOlog’un bu şantiyede arama yaptığı saatlerin dışındayız.',
          day_filed: 'Bu şantiyenin bugünkü raporu zaten kaydedildi.',
          call_live: 'Kendisiyle zaten bir arama sürüyor.',
          limit_reached: 'Ofis onu bugün izin verilen sayıda aradı.',
        },
      },
      callToday: {
        office: 'Ofis araması',
        reminder: 'Hatırlatma araması',
        line: '{{kind}} · {{progress}} · {{time}}',
      },
      callProgress: {
        requested: 'İstendi',
        ringing: 'Çalıyor',
        answered: 'Açıldı',
        not_reached: 'Ulaşılamadı',
        failed: 'Arama yapılamadı',
      },
      optOutStartOnly:
        'Mesajları yalnızca {{first}} yeniden açabilir: mesajlarımızdan birine START yazarak.',
      reporters: 'Saha raporcuları',
      columns: {
        name: 'Ad',
        mobile: 'Cep telefonu',
        prefers: 'Tercih',
        sites: 'Şantiyeler',
        language: 'Dil',
        onTime: 'Zamanında · 30 g',
        status: 'Durum',
      },
      roles: { site_manager: 'Şantiye şefi', backup: 'Yedek' },
      rowMenu: {
        edit: 'Düzenle',
        resendWelcome: 'Karşılama mesajını yeniden gönder',
        textLink: 'Günün linkini gönder',
        textLinkAt: 'Günün linkini gönder · {{site}}',
        revokeDevices: 'Cihazları iptal et',
        clearOptOut: 'Mesajları yeniden aç',
        callNow: 'Şimdi ara',
        callNowAt: 'Şimdi ara · {{site}}',
        enable: 'Etkinleştir',
        disable: 'Devre dışı bırak',
      },
      office: 'Ofis kullanıcıları',
      addOfficeUser: 'Ofis kullanıcısı ekle',
      officeRoles: {
        admin: 'Yönetici',
        pm: 'Proje müdürü',
        ops: 'Operasyon',
        accounts: 'Muhasebe',
      },
      uiLocales: {
        'en-US': 'İngilizce',
        'tr-TR': 'Türkçe',
      },
      officeLanguage: 'Panel dili: {{language}}',
      howMatching: 'Eşleştirme nasıl çalışır',
      howMatchingBody:
        'Her raporcunun telefon numarası ve kişisel linki kendi şantiyelerine bağlıdır. Bağlı bir numaradan gelen arama ya da mesaj o şantiyelere gider; bilinmeyen bir numara asla rapor oluşturmaz. Siz eşleştirene ya da yok sayana kadar burada bekler.',
      invite: {
        title: 'Raporcu davet et',
        name: 'Ad',
        phone: 'Cep telefonu',
        role: 'Görev',
        sites: 'Şantiyeler',
        languages: 'Konuştuğu diller',
        language: 'TalkOlog’un kullanacağı dil',
        preference: 'Tercih edilen kanal',
        preferenceHint: 'Diğerini de her zaman kullanabilir.',
        callTalkNow: 'Kayıt ekranındaki Şimdi konuş ile arar.',
        send: 'Karşılama mesajını gönder',
        smsAttest:
          '{{first}}, TalkOlog’dan günlük rapor mesajlarını SMS veya WhatsApp ile almayı kabul etti',
        smsAttestNoName:
          'Bu kişi, TalkOlog’dan günlük rapor mesajlarını SMS veya WhatsApp ile almayı kabul etti',
        smsAttestHint: 'Adınızla birlikte kayda geçer.',
        attestation_required:
          'Önce kutuyu işaretleyin: kişi günlük rapor mesajları almayı kabul etmiş olmalı.',
        description:
          'Raporcuya kişisel linkini mesajla gönderir. Linki telefonunda açar, numarasını bir kodla doğrular ve kaydı kabul eder.',
        namePlaceholder: 'Ad soyad',
        nameRequired: 'Bir ad girin.',
        sitesRequired: 'En az bir şantiye seçin.',
        noSites: 'Önce bir şantiye ekleyin. Her raporcu bir şantiye için rapor verir.',
        noSitesAskAdmin:
          'Önce bir yöneticiden şantiye eklemesini isteyin. Her raporcu bir şantiye için rapor verir.',
        addSite: 'Şantiye ekle',
        languagesHint: 'Raporlar her zaman {{language}} çıkar.',
        languageNames: {
          en: 'İngilizce',
          es: 'İspanyolca',
          pl: 'Lehçe',
          pt: 'Portekizce',
          hi: 'Hintçe',
          fr: 'Fransızca',
          tr: 'Türkçe',
        },
      },
      addUser: {
        title: 'Ofis kullanıcısı ekle',
        description: 'Sihirli bağlantılı bir e-posta alır ve seçtiğiniz rolle katılır.',
        email: 'E-posta',
        emailPlaceholder: 'ad@sirket.com',
        emailInvalid: 'Geçerli bir e-posta girin.',
        role: 'Rol',
        uiLanguage: 'Panel dili',
        uiLanguageOrg: 'Şirketin dili ({{language}})',
        send: 'Davet gönder',
        lastAdmin: 'Son yöneticiyi kaldıramazsınız.',
        roleHelp: {
          admin: 'Ayarlar ve faturalandırma dahil her şey',
          pm: 'Şantiyeler, raporlar, puantaj, hava durumu, sözlük',
          ops: 'Ayarlar ve faturalandırma dışında her şey',
          accounts: 'Yalnızca puantaj ve bordro',
        },
      },
      toasts: {
        invited: '{{name}} için karşılama mesajı sıraya alındı.',
        invitedOptedOut: '{{name}} mesajları durdurmuş (STOP); karşılama mesajı gönderilmedi.',
        linked: 'Numara eşleştirildi.',
        ignored: 'Numara yok sayıldı.',
        resent: 'Karşılama mesajı {{name}} kişisine yeniden gönderildi.',
        disabled: '{{name}} devre dışı bırakıldı.',
        enabled: '{{name}} etkinleştirildi. Yeni bir karşılama mesajı yolda.',
        updated: '{{name}} güncellendi.',
        devicesRevoked:
          '{{name}} tüm telefonlarda oturumu kapatıldı. Bir sonraki açılışta kod istenir.',
        optOutCleared: '{{name}} için mesajlar yeniden açıldı.',
        callRequested: '{{name}} şimdi aranıyor. Aramanın durumu satırında görünür.',
        memberAdded: 'Davet {{email}} adresine gönderildi.',
        memberUpdated: 'Rol güncellendi.',
        memberRemoved: 'Kullanıcı kaldırıldı.',
      },
      errors: {
        attestation_required: 'Önce kutuyu işaretleyin: bunu kişinin kendisi istemiş olmalı.',
        channel_not_offered: 'Bu kanal ülkenizde sunulmuyor.',
        locale_not_offered: 'Bu dil ülkenizde sunulmuyor.',
        two_way_pack: 'Mesajları yalnızca kişinin kendisi START yazarak yeniden açabilir.',
        opted_out_by_person: 'Mesajları kendisi durdurdu. Yalnızca kendisi yeniden açabilir.',
        not_opted_out: 'Bu numaraya mesajlar durdurulmamış.',
      },
    },
    // W4-05: P12 Settings (spec F22, §19 Q231–Q233), for the native-speaker read (Q63).
    settings: {
      title: 'Ayarlar',
      thing: 'ayarlar',
      tabs: {
        reports: 'Raporlar ve hatırlatmalar',
        pdf: 'PDF şablonu',
        recipients: 'Alıcılar',
        payroll: 'Bordro dışa aktarımı',
        fieldChannels: 'Saha kanalları',
      },
      save: 'Değişiklikleri kaydet',
      saved: 'Ayarlar kaydedildi.',
      deadline: 'Rapor son saati',
      workingDays: 'Çalışma günleri',
      ladder: 'Hatırlatma sırası',
      stages: {
        dailyLink: 'Günlük bağlantı',
        headsUp: 'Hava durumu uyarısı',
        reminder1: 'Hatırlatma 1',
        reminder2: 'Hatırlatma 2',
        missing: 'Eksik · ofis uyarılır',
      },
      offset: 'Fark (dakika)',
      offsetHint: '{{deadline}} son saatine göre dakika',
      offsetMinutes: '{{offset}} dk',
      stageTime: 'saat {{time}}',
      stageHints: {
        dailyLink: 'Her raporcu günün bağlantısını mesajla alır.',
        headsUp: 'Yalnızca tahminde yağmur veya rüzgâr olan günlerde',
        reminder1: 'Mesaj veya arama — kişiye göre',
        reminder2: 'İkinci mesaj; açıksa arama tercih edenlere ikinci arama',
        missing: 'Ofis bilgilendirilir ve şantiye günü eksik olarak işaretlenir',
      },
      dailyLinkChange: 'Saha kanallarında değiştir',
      required: 'Her raporda bulunması gerekenler',
      requiredHint:
        'Eksik bilgi için taslaktan önce kayıt ekranında veya aramada tek soru sorulur.',
      requiredFields: {
        manpower: 'Firmaya göre ekip',
        hours: 'Saatler',
        weather: 'Hava durumu',
        work_performed: 'Yapılan işler',
        safety: 'İş güvenliği',
        photo: 'En az bir fotoğraf',
      },
      filing: 'Kayda alma',
      filingConfirm: 'Raporcu onaylar — kayıt ekranında veya aramada sesle',
      filingConfirmHint:
        'Taslak, raporcu onayladıktan sonra kayda alınır. Yanıtlanmayan taslaklar 2 saat sonra kayda alınır.',
      filingAuto: '15 dakika sonra otomatik kayda al',
      filingAutoHint: 'En hızlı evrak; düzeltmeler yeni sürüm oluşturur.',
      signoffRequired: 'Onay gerekli',
      signoffRequiredHint: 'Her raporu bir ofis kullanıcısı panelde onaylar.',
      emailPdf: 'Kayda alınınca PDF’i e-postayla gönder',
      emailPdfHint: 'Rapor kayda alındığı anda alıcılar PDF’i alır.',
      deadlineHint: 'Yeni şantiyeler için varsayılan · her şantiye değiştirebilir',
      weekdays: {
        d1: 'Pzt',
        d2: 'Sal',
        d3: 'Çar',
        d4: 'Per',
        d5: 'Cum',
        d6: 'Cmt',
        d7: 'Paz',
      },
      workingDaysHint: 'Hatırlatmalar ve “eksik” yalnızca çalışma günlerinde sayılır.',
      channels: {
        pack: 'Ülke paketiniz',
        packHint: 'TalkOlog tarafından ülkeye göre ayarlanır. Değişiklik için TalkOlog’a yazın.',
        country: 'Ülke',
        sender: 'Mesaj göndericisi',
        senderTypes: {
          toll_free: 'Ücretsiz numara',
          long_code: 'Uzun numara',
          short_code: 'Kısa numara',
          alphanumeric: 'Gönderici adı',
        },
        senderNotSet: 'Bu ortamda ayarlanmadı',
        voiceNumber: 'TalkOlog’u arama numarası',
        talkNowOnly: 'Yalnızca Şimdi konuş',
        channelOrder: 'Mesaj sırası',
        twoWay: 'Mesajlara yanıt',
        twoWayYes: 'Çift yönlü: STOP, HELP ve yanıtlar TalkOlog’a ulaşır',
        twoWayNo: 'Tek yönlü: mesajlar yanıtlanamaz',
        links: 'Mesajlarda bağlantı',
        linksYes: 'İzinli',
        linksNo: 'İzinli değil',
        packMissing: 'Ülke paketiniz yüklenemedi.',
        dailyLink: 'Günlük bağlantı',
        dailyLinkTime: 'Günlük bağlantı saati',
        dailyLinkTimeHint: 'Her raporcu günün bağlantısını bu saatte mesajla alır (şantiye saati).',
        ttl: 'Günlük bağlantının geçerlilik süresi (saat)',
        ttlHint: 'Boş: TalkOlog’un varsayılanı. Tam saat, 1–168.',
        ttlInvalid: '1 ile 168 arasında tam saat girin veya boş bırakın.',
        calls: 'Aramalar',
        autoCall: 'Arama tercih edenleri ara',
        autoCallHint: 'Hatırlatma 1’de, arama aralığı içinde, aramaya onay verdilerse.',
        secondCall: 'Hatırlatma 2’de ikinci arama',
        secondCallHint: 'Hatırlatma mesajıyla birlikte ikinci bir arama.',
        callWindow: 'Arama aralığı',
        callWindowHint:
          'Şantiye saati. Aramalar ve hatırlatma mesajları yalnızca bu aralıkta gider.',
        callWindowFrom: 'Başlangıç',
        callWindowTo: 'Bitiş',
        noCalls:
          'TalkOlog {{country}} içinde henüz arama yapmıyor. Arama tercih edenler kayıt ekranında Şimdi konuş’u kullanır; hatırlatma mesajları arama aralığına uyar.',
        newReporters: 'Yeni raporcular',
        defaultPref: 'Varsayılan tercih',
        defaultPrefHint: 'Kurulum ve Davet bununla başlar. Diğerini her zaman kullanabilirler.',
        notice: 'Kayıt bildirimi',
        noticeHint: 'Her aramanın başında söylenir. TalkOlog ülkeye göre belirler ve sürümler.',
        noticeVersion: 'Sürüm {{version}}',
        test: 'Deneme',
        testHint: 'Şantiye şeflerinizin ne aldığını görün.',
        testMessage: 'Bana deneme mesajı gönder',
        testDialog: {
          title: 'Bana deneme mesajı gönder',
          body: 'TalkOlog bu numaraya ülkenizin göndericisinden bir deneme mesajı gönderir. Numara kaydedilmez.',
          phone: 'Cep telefonu numaranız',
          language: 'Mesajın dili',
          attest: 'Bu numara bana ait.',
          attestRequired: 'Numaranın size ait olduğunu onaylamak için kutuyu işaretleyin.',
          send: 'Deneme mesajı gönder',
        },
        testResults: {
          sent: 'Deneme mesajı gönderildi. Bir dakika içinde ulaşması gerekir.',
          demo: 'Demo şirket: hiçbir şey gönderilmedi. Gerçek şirketler mesajı ülkenin göndericisinden alır.',
          noSender:
            'Bu ortamda {{country}} için mesaj göndericisi ayarlanmadı, bu yüzden hiçbir şey gönderilmedi.',
          smsOff:
            '{{country}} için SMS kapalı: TalkOlog şantiye şeflerine WhatsApp ve uygulama bildirimleriyle ulaşıyor, bu yüzden hiçbir şey gönderilmedi.',
          failed: 'Mesaj sağlayıcısı deneme mesajını reddetti. TalkOlog bunu kaydetti.',
          rateLimited: 'Saatte beş deneme mesajı. Daha sonra tekrar deneyin.',
          invalidPhone: 'Geçerli bir cep telefonu numarası girin.',
        },
        callMeNow: 'Beni şimdi ara',
        callDialog: {
          title: 'Beni şimdi ara',
          body: 'TalkOlog bu numarayı kısa bir deneme aramasıyla arar: kimin aradığını, tek bir cümleyi ve vedayı söyler. Hiçbir şey kaydedilmez, numara saklanmaz.',
          phone: 'Cep telefonu numaranız',
          language: 'Aramanın dili',
          attest: 'Bu numara bana ait.',
          attestRequired: 'Numaranın size ait olduğunu onaylamak için kutuyu işaretleyin.',
          call: 'Beni şimdi ara',
        },
        callResults: {
          requested: 'Aranıyorsunuz. Telefonunuz bir dakika içinde çalmalı.',
          demo: 'Demo şirket: arama TalkOlog konsolunda yapıldı, gerçek bir telefon çalmaz.',
          rateLimited: 'Saatte beş deneme araması. Daha sonra tekrar deneyin.',
          callLive: 'Bir deneme araması hâlâ sürüyor. Bitmesini bekleyin.',
          noTrunk: 'TalkOlog henüz {{country}} içinde arama yapmıyor.',
          invalidPhone: 'Geçerli bir cep telefonu numarası girin.',
        },
      },
      pdf: {
        logo: 'Logo',
        logoHint: '2 MB’a kadar PNG veya SVG · her PDF’te gösterilir',
        upload: 'Logo yükle',
        replace: 'Logoyu değiştir',
        removeLogo: 'Logoyu kaldır',
        logoDemo: 'Demo modu: logo yalnızca bu tarayıcı sekmesinde tutulur.',
        logoUploading: 'Yükleniyor…',
        logoWrongType: 'PNG veya SVG dosyası kullanın.',
        logoTooLarge: 'Logo 2 MB veya daha küçük olmalı.',
        letterhead: 'Antet satırları',
        letterheadHint: 'Adres, ruhsat numarası — şirket adının altında, her satıra bir tane.',
        letterheadPlaceholder: 'Satır',
        letterheadLine: 'Satır {{n}}',
        addLine: 'Satır ekle',
        removeLine: 'Satırı kaldır',
        removeLineN: '{{n}}. satırı kaldır',
        footer: 'Alt bilgi',
        footerText: 'Şantiye şefinin sesli raporundan TalkOlog tarafından oluşturuldu',
        footerToggle: '“TalkOlog tarafından oluşturuldu…” alt bilgisini göster',
        paper: 'Kâğıt boyutu',
        letter: 'Letter',
        a4: 'A4',
        photos: 'Rapor başına fotoğraf',
        photosHint: 'Günlük PDF’te en fazla 4.',
        preview: 'Önizleme · sayfa 1',
        previewReport: 'Günlük rapor',
        previewNo: 'RL-2041-0187 · s2',
        previewLogo: 'LOGO',
        reportLocale: 'Rapor dili ve birimler',
        reportLocaleHint:
          'Yeni rapor sürümleri bunları kullanır. Kayda alınmış sürümler kendilerininkini korur.',
        reportLanguage: 'Rapor dili',
        reportLanguages: { en: 'İngilizce', tr: 'Türkçe' },
        units: 'Birimler',
        unitNames: {
          imperial: 'Emperyal (°F, inç, mph)',
          metric: 'Metrik (°C, mm, km/sa)',
        },
        reportLocaleApply: 'Dili ve birimleri değiştir',
        reportLocaleConfirmTitle: 'Rapor dili ve birimler değişsin mi?',
        reportLocaleConfirmBody:
          'Yeni rapor sürümleri {{language}}, {{units}} olacak. Kayda alınmış sürümler dillerini ve birimlerini korur.',
        reportLocaleConfirm: 'Değiştir',
        reportLocaleSaved: 'Rapor dili ve birimler kaydedildi.',
      },
      recipients: {
        perSite: 'Şantiye bazında alıcılar',
        hint: 'Kayda alınan PDF’ler ve günlük özet bu adreslere gider.',
        applyAll: 'Tüm şantiyelere uygula',
        applyAllFor: '{{site}} listesini diğer tüm şantiyelere kopyalar.',
        applyAllSite: 'Tüm şantiyelere uygula · {{site}}',
        applied: 'Alıcılar tüm şantiyelere uygulandı.',
        add: 'E-posta ekle',
        addSite: 'E-posta ekle · {{site}}',
        placeholder: 'ad@firma.com',
        removeEmail: '{{email}} adresini kaldır',
        perSiteOf: 'Şantiye bazında alıcılar · {{site}}',
        invalid: 'Geçerli bir e-posta girin.',
        none: 'Henüz alıcı yok.',
        noSitesTitle: 'Henüz şantiye yok',
        noSitesBody: 'Alıcılar şantiye bazında ayarlanır. Önce bir şantiye ekleyin.',
      },
      payroll: {
        format: 'Biçim',
        formats: { generic: 'Genel CSV' },
        formatHint: 'Başka hazır biçimler (QuickBooks, ADP) yolda.',
        columns: 'Sütunlar',
        columnsHint: 'Bordro CSV’sinin sütun sırası, şirketteki herkes için.',
        reset: 'Varsayılan sıraya dön',
        moveUpCol: '{{column}} sütununu yukarı taşı',
        moveDownCol: '{{column}} sütununu aşağı taşı',
        includeUnconfirmed: 'Onaylanmamış firmaları dahil et',
        includeUnconfirmedHint:
          'Raporlarda duyulan ama henüz taşeron olarak onaylanmamış satırlar.',
      },
      unsavedTitle: 'Kaydedilmemiş değişiklikler',
      unsavedBody: 'Kaydedilmemiş ayarlarınız var. Kaydetmeden çıkılsın mı?',
      leave: 'Çık',
      stay: 'Kal',
      discard: 'Değişiklikleri at',
    },
    states: {
      error: '{{thing}} yüklenemedi.',
      errorId: 'Hata kimliği {{id}}',
      retry: 'Tekrar dene',
      noAccessTitle: 'Erişim yok',
      noAccess: '{{page}} sayfasına erişiminiz yok. Bir yöneticiye danışın.',
      goToHours: 'Puantaja git',
      goHome: 'Genel bakışa git',
      notFoundTitle: 'Sayfa bulunamadı',
      notFound: 'Bu sayfa yok ya da link yanlış yazılmış.',
      comingNextTitle: 'Yakında',
      comingNext: 'Bu sayfa hazırlanıyor, yakında kullanıma açılacak.',
    },
    a11y: {
      searchResults: 'Arama sonuçları',
      loadingPage: 'Sayfa yükleniyor',
      statusChipLabel: 'Durum: {{label}}',
      lateBadge: 'Son saatten sonra dosyalandı',
      kpiBreakdown: 'Dağılım: {{parts}}',
      closeDialog: 'Pencereyi kapat',
      heatmapLegend: 'Kayıp saatler, açıktan koyuya',
      menuForName: '{{name}} için menü',
    },
    login: {
      headline: 'Gün sonu raporları, üç dakikada anlatılır.',
      bullets: {
        link: 'Şantiye şefleri mesajdaki linke dokunur ya da aramayı yanıtlar; kurulacak bir şey yok.',
        pdf: 'Günlük şantiye raporu düzgün bir PDF olarak buraya gelir, dosyalanmaya hazır.',
        trace:
          'Taşeron saatleri ve hava kaynaklı gecikmeler; her biri ses kaydına kadar izlenebilir.',
      },
      tagline: 'Anlatın. Kayda geçsin.',
      title: 'Ofis paneline giriş yapın',
      subtitle:
        'Size e-postayla tek kullanımlık bir link göndereceğiz. Hatırlamanız gereken bir şifre yok.',
      emailLabel: 'İş e-postası',
      emailPlaceholder: 'ad@sirket.com',
      emailInvalid: 'Geçerli bir iş e-postası girin.',
      submit: 'Bana giriş linki gönder',
      sending: 'Gönderiliyor…',
      or: 'veya',
      sso: 'Kurumsal SSO ile devam et',
      ssoSoon: 'Yakında',
      managersNoteLead: 'Şantiye şefleri giriş yapmaz.',
      managersNote: 'Mesajdaki link ya da telefon aramasıyla rapor verirler.',
      footer: 'Yeni misiniz?',
      footerCta: 'Şirket hesabınızı açın',
      footerSuffix: '— kurulum ücreti yok.',
      sentTitle: 'Gelen kutunuza bakın',
      sent: '{{email}} adresine bir giriş linki gönderdik. Link 1 saat geçerli.',
      resend: 'Linki yeniden gönder',
      resendIn: '{{seconds}} sn sonra yeniden gönder',
      useAnother: 'Başka bir e-posta kullan',
      expired: 'Bu linkin süresi doldu. Aşağıdan yenisini isteyin.',
      authError: 'Giriş yapılamadı. Tekrar deneyin.',
      rateLimited: 'Çok fazla giriş e-postası istendi. Bir dakika bekleyip tekrar deneyin.',
      demoHint:
        'Demo modu: hangi e-postayı girerseniz girin Lena Ortiz (yönetici) olarak giriş yaparsınız.',
      demoHintAs: 'Demo modu: {{name}} olarak giriş yapacaksınız.',
      callbackTitle: 'Giriş yapılıyor…',
      callbackBody: 'Linkinizi kontrol ediyoruz, bir saniye.',
      accountThing: 'şirketleriniz',
      signOut: 'Çıkış yap',
    },
    errorBoundary: {
      title: 'Bir sorun oluştu',
      body: 'TalkOlog beklenmeyen bir hatayla karşılaştı. Tekrar deneyin ya da çıkış yapıp yeniden giriş yapın.',
      retry: 'Tekrar dene',
      signOut: 'Çıkış yap',
      reported: 'TalkOlog ekibine bildirildi.',
      errorId: 'Hata kimliği {{id}}',
    },
    /** W3-17 (M8): the /ops card of reminder calls (the rest of /ops is TalkOlog staff English). */
    ops: {
      outboundCalls: {
        title: 'Hatırlatma aramaları (M8)',
        hint: 'Kuruluş ve hafta bazında: TalkOlog’un rapor veren için aradığı şantiye günleri ve bunlardan kaçının, ofis yardımı olmadan, rapor verenin “evet”iyle o aramada kaydedildiği. M8 hedefi %85.',
        columns: {
          org: 'Kuruluş',
          week: 'Hafta',
          called: 'Aranan',
          filed: 'Aramada kaydedilen',
          share: 'Oran',
        },
        share: '%{{pct}}',
        belowTarget: '%85 hedefinin altında',
        demo: 'Demo',
        empty: 'Son 8 haftada hatırlatma araması yok.',
      },
    },
    devPhone: {
      subtitle:
        'TalkOlog’un konsol sürücüsüyle gönderdiği tüm mesajlar; her rapor veren için ayrı bir konuşma.',
      tabsLabel: 'Demo telefon görünümleri',
      tabTexts: 'Mesajlar',
      tabEmail: 'E-posta',
      reportersLabel: 'Rapor verenler',
      textsCount: '{{count}} mesaj',
      noTexts: 'Henüz mesaj yok',
      pickReporter: 'Mesajlarını görmek için bir rapor veren seçin.',
      back: 'Tüm rapor verenler',
      threadLabel: '{{name}} ile mesajlar',
      threadEmpty: '{{name}} ile henüz mesaj yok.',
      fromTalkOlog: 'TalkOlog',
      opensInNewTab: '(yeni sekmede açılır)',
      copyKey: 'Mesaj',
      channelLabel: 'Kanal',
      channels: {
        sms: 'SMS',
        push: 'Anlık bildirim',
        whatsapp: 'WhatsApp',
        call: 'Arama',
      },
      call: {
        ringing: 'TalkOlog arıyor: çalıyor',
        answered: 'TalkOlog ile görüşmede',
        ended: 'TalkOlog ile görüşme bitti',
        missed: 'TalkOlog’dan cevapsız arama',
        busy: 'TalkOlog’un araması: meşgul',
        voicemail: 'TalkOlog’un araması telesekretere düştü: mesaj bırakılmadı',
        failed: 'TalkOlog’un araması yapılamadı',
      },
      callPurpose: {
        reminder: 'Hatırlatma araması',
        callback: 'Geri arama',
        daily_report: 'Ofisten arama',
      },
      statusLabel: 'Durum',
      status: {
        queued: 'Gönderiliyor',
        sent: 'Gönderildi',
        delivered: 'Teslim edildi',
        read: 'Okundu',
        received: 'Alındı',
        failed: 'Başarısız',
        undeliverable: 'Teslim edilemedi',
      },
      replyLabel: '{{name}} olarak yanıtla',
      replyLabelWhatsapp: "{{name}} olarak WhatsApp'tan yanıtla",
      replyChannel: 'Yanıt kanalı',
      replyPlaceholder: 'Bir mesaj yazın, ör. HELP veya STOP',
      send: 'Gönder',
      replyFailed: 'Yanıt gönderilemedi.',
      oneWay:
        'Yanıtlar kapalı: {{code}} ülke paketinde SMS tek yönlü, rapor veren TalkOlog’a mesaj gönderemez.',
      oneWayWhatsapp:
        '{{code}} ülke paketinde SMS tek yönlü: rapor veren TalkOlog’a WhatsApp’tan yanıt verir.',
      maskedNote: 'Bu bir demo şirket değil: link kodları gizlenir ve buradan mesaj yazılamaz.',
      notDemoTitle: 'Demo telefon demo şirketler içindir',
      notDemoBody:
        'Bu şirketin mesajları gerçek telefonlara gider. Demo telefonu kullanmak için bir demo şirkete geçin.',
      emptyTitle: 'Henüz rapor veren yok',
      emptyBody:
        'Ekip ve numaralar sayfasından bir rapor veren davet edin: karşılama mesajı burada görünür.',
      emailLabel: 'E-postalar',
      emailEmpty:
        'Henüz e-posta yok. Çalışan konsol e-posta sağlayıcısını kullandığı sürece göndereceği her e-posta burada görünür.',
      emailTo: 'Alıcı: {{to}}',
      attachments: 'Ekler',
      attachmentSize: '{{name}} · {{size}} KB',
      thing: 'demo telefon',
      threadThing: 'mesajlar',
      emailThing: 'e-postalar',
    },
    /** P4 streak bar (W1-18). */
    streak: {
      legend: {
        on_time: 'Zamanında',
        late: 'Geç',
        weather_day: 'Hava engeli',
        missing: 'Eksik',
        pending: 'Bekleniyor',
      },
      summary: '{{total}} günün {{onTime}} gününde zamanında',
      cellLabel: '{{date}}: {{status}}',
    },
    /** P5 calendar (W1-18). */
    calendar: {
      previousMonth: 'Önceki ay',
      nextMonth: 'Sonraki ay',
      weekend: 'Hafta sonu',
      today: 'Bugün',
      noReport: 'Rapor yok',
      crew: '{{count}} kişi',
      crewUnit: 'kişi',
      late: 'Geç',
      openReport: '{{date}} raporunu aç',
      weatherAffected: 'Hava etkiledi',
      legend:
        'Hafta sonları gri · raporunu açmak için bir güne dokunun · bulut = hava gecikmesi kaydedildi',
      channel: {
        link: 'Mesajdaki link',
        call: 'Arama',
        office: 'Panelde yazıldı',
      },
      channelLegend: 'Raporların nasıl geldiği',
    },
    /** P4 Sites (W1-18). */
    sites: {
      title: 'Şantiyeler',
      subtitle: '{{count}} aktif · her biri her gün mesajdaki linkle ya da telefonla rapor verir',
      addSite: 'Şantiye ekle',
      tabs: { all: 'Tümü', attention: 'İlgi gereken', paused: 'Duraklatılan' },
      legend: 'Son 14 iş günü',
      lastWorkdays: 'Son 14 iş günü · {{onTime}} gün zamanında',
      crewToday: '{{count}} kişi',
      crewTodayNone: '— kişi',
      weatherDays: '{{count}} hava engeli günü ({{month}})',
      weatherDaysNone: 'Hava engeli günü yok ({{month}})',
      connectAnother: 'Başka bir şantiye bağlayın',
      connectAnotherPrice: 'Aylık {{price}} · kurulum ücreti yok',
      emptyAttention: 'Şu anda ilgilenmeniz gereken bir şantiye yok.',
      emptyPaused: 'Duraklatılmış şantiye yok.',
      empty: 'Henüz şantiye yok',
      emptyBody:
        'İlk şantiyenizi bağlayın, ardından günlük raporlarını Raporlar → Yeni rapor üzerinden dosyalayın.',
      emptyBodyAskAdmin:
        'Şantiyeleri yöneticiler bağlar. İlk şantiyeyi bağlaması için bir yöneticiye danışın.',
      connectFirstSite: 'İlk şantiyenizi bağlayın',
      openSite: '{{name}} şantiyesini aç',
      cardLabel: 'Şantiye kartı: {{name}}',
      pausedSince: '{{date}} tarihinden beri duraklatıldı',
      paused: 'Duraklatıldı',
      reporter: 'Şantiye şefi',
      prefers: {
        link: 'Tercih: mesajla link',
        call: 'Tercih: telefon araması',
      },
      filterLabel: 'Şantiyeleri filtrele',
    },
    /** P5 Site detail (W1-18). */
    siteDetail: {
      breadcrumb: 'Şantiyeler',
      breadcrumbLabel: 'Konum',
      sectionsLabel: 'Şantiye bölümleri',
      tabs: {
        reports: 'Günlük raporlar',
        crew: 'Ekip ve taşeronlar',
        weather: 'Hava ve gecikmeler',
        photos: 'Fotoğraflar',
      },
      siteSettings: 'Şantiye ayarları',
      call: 'Ara: {{first}}',
      callHint: '{{phone}} numarasını kendi telefonunuzdan arar',
      textLink: 'Günün linkini gönder',
      newReport: 'Yeni rapor',
      latest: 'Son rapor · {{date}}',
      openReport: 'Raporu aç',
      thisMonth: 'Bu ay şimdiye kadar',
      crewToday: 'Bugünkü ekip, firmaya göre',
      stats: {
        reports: 'Raporlar',
        reportsOf: '{{workdays}} iş gününün {{count}} günü',
        onTime: 'Zamanında',
        weatherHours: 'Hava kaynaklı saat',
        laborHours: 'İşçilik saati',
        avgCrew: 'Ort. ekip',
      },
      columns: { company: 'Firma', trade: 'İş kolu · alan', workers: 'Çalışan', hours: 'Saat' },
      total: 'Toplam',
      viewAll: 'Tümünü gör',
      deadline: 'Rapor son saati {{time}} {{zone}}',
      pauseSite: 'Şantiyeyi duraklat',
      resumeSite: 'Şantiyeyi sürdür',
      speaks: '{{languages}} konuşur',
      languagesAnd: '{{first}} ve {{last}}',
      backup: 'Yedek',
      primary: 'Şantiye şefi',
      latestEmpty: 'Henüz dosyalanmış rapor yok.',
      summaryEmpty: 'Özet yok.',
      latestWeatherDay: 'Hava engeli',
      trio: { crew: 'Ekip', hours: 'Saat', weatherHours: 'Hava kaynaklı saat' },
      crewTodayEmpty: 'Bugün ekip bildirilmedi.',
      badges: {
        edited: 'Düzenlendi v{{version}}',
        asked: 'Soruldu {{time}}',
        confirmedByAnswer: 'Yanıtla onaylandı',
        unconfirmed: 'Onaylanmadı',
      },
      crewTab: {
        title: 'Bu hafta ekip ve taşeronlar',
        subtitle: 'Bu şantiyede bildirilen saatler · {{week}}',
        openHours: 'Puantajı aç',
        previousWeek: 'Önceki hafta',
        nextWeek: 'Sonraki hafta',
        empty: 'Bu hafta bu şantiyede saat bildirilmedi.',
        columns: { company: 'Firma', reported: 'Bildirilen' },
        thing: 'puantaj',
      },
      weatherTab: {
        title: 'Gecikme kaydı',
        subtitle: 'Bu şantiyede kaybedilen saatler · {{month}}',
        openWeather: 'Hava ve gecikmeleri aç',
        empty: 'Bu ay hava kaynaklı gecikme yok.',
        lostLabel: 'Kayıp',
        fullDay: 'Tüm gün',
        period: '{{start}}–{{end}}',
        periodFrom: '{{start}} itibarıyla',
        periodUntil: '{{end}} saatine kadar',
        observed: 'Ölçülen yağış {{precip}}',
        thing: 'hava ve gecikmeler',
      },
      photosTab: {
        title: '{{count}} fotoğraf',
        dateFilter: 'Tarih',
        allDates: 'Tüm tarihler',
        empty:
          'Henüz fotoğraf yok. Şantiye şefleri fotoğrafları ses kaydıyla gönderir; ofis, Düzenle ile rapora ekler.',
        open: 'Fotoğrafı aç: {{caption}}',
        noPreview: 'Önizleme yok',
        openReport: '{{no}} raporunu aç',
        caption: '{{date}} · {{time}}',
        taken: 'Çekim: {{time}}',
      },
      settings: {
        title: 'Şantiye ayarları',
        name: 'Şantiye adı',
        projectCode: 'Proje kodu',
        address: 'Adres',
        addressHint: 'Gecikme kaydı için yerel hava verisini almakta kullanılır.',
        lat: 'Enlem',
        lng: 'Boylam',
        timezone: 'Saat dilimi',
        deadline: 'Şantiyeye özel son saat',
        deadlineHint: 'Şirket varsayılanını ({{org}}) kullanmak için boş bırakın.',
        workingHours: 'Çalışma saatleri',
        workingHoursHint: 'ör. 07:00–15:30',
        reporters: 'Rapor verenler',
        reportersHint: 'Rapor verenler Ekip ve numaralar sayfasından yönetilir.',
        noReporters: 'Henüz bağlı rapor veren yok.',
        inviteSent: 'Davet gönderildi',
        recipients: 'PDF alıcıları',
        recipientsHint: 'Her satıra bir e-posta yazın ya da virgülle ayırın.',
        recipientsInvalid: '“{{email}}” geçerli bir e-posta değil.',
        nameRequired: 'Şantiye adı gerekli.',
        codeRequired: 'Proje kodu gerekli.',
        timeInvalid: 'SS:DD (24 saat) biçimini kullanın.',
        latLngInvalid: 'Bir sayı girin.',
        pauseTitle: '{{site}} duraklatılsın mı?',
        pauseBody:
          'Bu şantiyeden rapor gelmez ve bir sonraki dönemden itibaren faturalandırılmaz. İstediğiniz zaman sürdürebilirsiniz.',
        pauseConfirm: 'Şantiyeyi duraklat',
        resumeTitle: '{{site}} sürdürülsün mü?',
        resumeBody:
          'Günlük raporlar ve hatırlatmalar bir sonraki iş gününden itibaren yeniden başlar.',
        resumeConfirm: 'Şantiyeyi sürdür',
        pausedNote: '{{date}} tarihinden beri duraklatıldı',
        cancel: 'Vazgeç',
        save: 'Değişiklikleri kaydet',
      },
    },
    /** W3-06: P8 · Ekip saatleri (F19), with the invoice check and the Add invoice dialog. */
    hours: {
      title: 'Ekip saatleri',
      subtitle: 'Firmaya göre taşeron saatleri, faturalarla karşılaştırılmış',
      thing: 'ekip saatleri',
      payrollCsv: 'Bordro CSV',
      weekPicker: 'Hafta',
      previousWeek: 'Önceki hafta',
      nextWeek: 'Sonraki hafta',
      siteSelect: 'Şantiye',
      allSites: 'Tüm şantiyeler',
      kpisLabel: 'Temel göstergeler',
      kpis: {
        subHours: 'Taşeron saatleri',
        companies: 'Sahadaki firmalar',
        gaps: 'Bulunan fatura farkları',
        gapsDetail: '{{hours}} · {{count}} taşeron',
      },
      columns: {
        company: 'Firma',
        reported: 'Raporlanan',
        invoiced: 'Faturalanan',
        gap: 'Fark',
      },
      gap: {
        match: 'eşleşiyor',
        awaiting: 'bekleniyor',
        confirm: 'Doğrula',
        unconfirmed: 'Doğrulanmadı',
      },
      addInvoice: 'Fatura ekle',
      invoiceCheck: 'Fatura kontrolü',
      invoice: '{{no}} · {{period}}',
      compare: { day: 'Gün', invoice: 'Fatura', reports: 'Raporlar', gap: 'Fark' },
      evidence: 'Kanıt',
      sendQuery: 'Kanıtla birlikte soru gönder',
      markResolved: 'Çözüldü olarak işaretle',
      queried: 'Soru gönderildi',
      resolved: 'Çözüldü olarak işaretlendi',
      empty: 'Bu hafta raporlanan saat yok.',
      selectRow: 'Faturayla karşılaştırmak için farkı olan bir satır seçin.',
      selectRowTitle: 'Fatura kontrolü',
      perCompanyHint:
        'Faturalar firma bazında kontrol edilir. Karşılaştırmak için Tüm şantiyeler seçeneğini seçin.',
      unconfirmedHint: 'Bir raporda geçti, ancak henüz bilinen bir taşeron değil.',
      unconfirmedAsk:
        'Bir raporda geçti, ancak henüz bilinen bir taşeron değil. Doğrulamasını bir operasyon kullanıcısından isteyin.',
      openRow: '{{company}} firmasını faturasıyla karşılaştır',
      totalRow: 'Toplam',
      evidenceFor: '{{date}} · {{site}}',
      evidenceKinds: {
        voiceNote: 'sesli not',
        answer: 'yanıt',
        report: 'rapor',
      },
      openReport: '{{no}} raporunu aç',
      openReportAt: '{{no}} raporunu {{source}} noktasında aç',
      crewQuote: '{{company}} · {{lines}} = {{total}}',
      crewLine: '{{workers}} × {{hours}}',
      noEvidence: 'Kanıt gösterilecek fark günü yok.',
      noReportEvidence: 'Fark günlerinde hiçbir şantiye raporu bu firma için saat kaydetmedi.',
      queryMail: {
        subject: 'Fatura {{no}} — TalkOlog saat sorusu',
        intro:
          'Merhaba {{company}},\n\n{{no}} numaralı fatura ({{period}}) günlük şantiye raporlarımızdan daha fazla saat gösteriyor. Gün gün:',
        row: '{{date}}: faturalanan {{invoiced}}, raporlanan {{reported}}, fark {{gap}}',
        total: 'Toplam: faturalanan {{invoiced}}, raporlanan {{reported}}, fark {{gap}}',
        evidence: 'Şantiye raporlarındaki kanıt:',
        evidenceLine: '- {{date}} · {{site}} · {{reportNo}} · {{source}}: “{{quote}}”',
        outro:
          'Bu günleri kontrol edip düzeltilmiş bir fatura ya da farkın dayandığı puantajları gönderebilir misiniz?\n\nTeşekkürler,',
      },
      addDialog: {
        title: 'Fatura ekle',
        description: 'Bu haftanın bir taşeron faturasını kaydedin ve raporlarla karşılaştırın.',
        company: 'Firma',
        companyPlaceholder: 'Bir firma seçin',
        invoiceNo: 'Fatura numarası',
        invoiceNoPlaceholder: 'ör. BL-3381',
        periodStart: 'Dönem başlangıcı',
        periodEnd: 'Dönem sonu',
        hours: 'Günlük saatler',
        hoursHint: 'Bu haftanın raporlarından dolduruldu. Faturaya uyacak şekilde değiştirin.',
        total: 'Fatura toplamı',
        file: 'Fatura PDF dosyası',
        fileHint: 'İsteğe bağlı · en fazla 10 MB PDF',
        save: 'Fatura ekle',
        companyRequired: 'Bir firma seçin.',
        invoiceNoRequired: 'Fatura numarasını girin.',
        periodInvalid: 'Dönem sonu, başlangıçla aynı gün ya da sonrası olmalı.',
        hoursInvalid: 'Saatler sayı olmalı.',
        noCompanies:
          'Henüz taşeron yok. Dosyalanan bir rapor bir firmanın ekibini listelediğinde firma burada görünür.',
      },
    },
    /** P11 Şantiye sözlüğü (W3-08). */
    glossary: {
      title: 'Şantiye sözlüğü',
      subtitle: 'Sahada söylenenler, raporlarda yazılması gerektiği gibi',
      thing: 'şantiye sözlüğü',
      addTerm: 'Terim ekle',
      reviewTitle: 'Bu hafta yeni · {{n}} inceleme',
      heard: '{{times}} kez duyuldu',
      heardAt: '{{times}} kez duyuldu · {{site}}',
      approve: 'Onayla',
      change: 'Değiştir',
      reject: 'Reddet',
      addAsSub: 'Taşeron olarak ekle',
      mapTo: 'Eşleştir…',
      all: 'Tüm terimler',
      tabs: { all: 'Tümü', companies: 'Firmalar', spanish: 'İspanyolca', turkish: 'Türkçe' },
      columns: {
        heard: 'Sahada duyulan',
        official: 'Raporlarda yazılışı',
        scope: 'Kapsam',
        times: 'Sayı',
        source: 'Kaynak',
        actions: 'İşlemler',
      },
      scope: { org: 'Tüm şantiyeler' },
      search: 'Terim ara',
      empty: 'Henüz terim yok. Asistan terimleri sesli notlardan öğrenir.',
      emptyReview: 'İncelenecek bir şey yok.',
      emptyReviewBody:
        'Sesli notlardaki yeni ifadeler, haftanın raporları geldikçe burada görünür.',
      form: {
        title: 'Terim ekle',
        description:
          'Asistana sahada kullanılan bir ifadeyi ve raporlarda nasıl yazılacağını öğretin.',
        heard: 'Sahada duyulan',
        heardPlaceholder: 'ör. döküm',
        official: 'Raporlarda yazılışı',
        officialPlaceholder: 'ör. Beton dökümü',
        kind: 'Tür',
        language: 'Dil',
        scope: 'Kapsam',
        allSites: 'Tüm şantiyeler',
        save: 'Terim ekle',
        heardRequired: 'İfadeyi duyulduğu gibi girin.',
        officialRequired: 'Resmî terimi girin.',
      },
      kinds: { term: 'Terim', company: 'Firma', area: 'Alan', unit: 'Birim' },
      languages: {
        en: 'İngilizce',
        es: 'İspanyolca',
        tr: 'Türkçe',
        pl: 'Lehçe',
        hi: 'Hintçe',
        pt: 'Portekizce',
      },
      arrow: 'şöyle yazılır',
      unknownCompany: 'Bilinmeyen firma',
      changeLabel: 'Resmî terim',
      saveChange: 'Kaydet',
      mapToLabel: 'Mevcut bir firmayla eşleştir',
      mapToPlaceholder: 'Bir firma seçin',
      mapConfirm: 'Eşleştir',
      noResults: '“{{q}}” ile eşleşen terim yok.',
      /** W4-08 (§19 Q238): TalkOlog'un hazır başlangıç terimleri. */
      builtIn: {
        changeTitle: 'Yerleşik terimi değiştir',
        changeDescription:
          '“{{heard}}” raporlarda nasıl yazılsın? Kaydettiğinizde terim firmanızın onaylı terimi olur.',
        changeTerm: '“{{heard}}” terimini değiştir',
        rejectTerm: '“{{heard}}” terimini reddet',
      },
    },
    /** P9 / F20 kanıt etiketleri (W3-07). */
    evidence: {
      voiceNote: 'Sesli not {{ts}}',
      call: 'Arama {{ts}}',
      openVoiceNote: '{{no}} raporunu sesli notun {{ts}} anında aç',
      openCall: '{{no}} raporunu aramanın {{ts}} anında aç',
      photos: '{{count}} fotoğraf',
      weatherData: 'Hava verisi',
      photoRequested: 'Fotoğraf istendi',
      photoMissing: 'Fotoğraf yok',
      audioMissing: 'Ses kaydı yok',
      weatherMissing: 'Hava verisi yok',
      claimReady: 'Talebe hazır',
    },
    /** P9 Hava durumu ve gecikmeler (W3-07). */
    weather: {
      title: 'Hava durumu ve gecikmeler',
      subtitle:
        'Kaybedilen her saat, arkasındaki sesli not veya arama, fotoğraflar ve hava verisiyle',
      thing: 'hava durumu ve gecikmeler',
      buildPack: 'Süre uzatımı kanıt dosyası oluştur',
      kpisLabel: 'Temel göstergeler',
      kpis: {
        siteDays: 'Hava engelli şantiye günleri',
        hours: 'Duruş saatleri',
        claimReady: 'Talebe hazır kayıtlar',
        claimReadyHint: 'ses + fotoğraf + hava verisi',
      },
      heatmap: 'Şantiye ve güne göre duruş saatleri',
      heatmapCell: '{{site}}, {{date}}: {{hours}} kayıp',
      log: 'Gecikme kaydı',
      fullDay: 'Tüm gün',
      observed: 'Ölçülen yağış {{precip}}',
      notConfirmed: 'Şantiye şefi onaylamadı: ofis onaylayınca talebe hazır sayılır',
      pack: {
        title: 'Süre uzatımı kanıt dosyası',
        site: 'Şantiye',
        period: 'Dönem',
        sections: 'Dahil edilen bölümler',
        sectionNames: {
          summary: 'Özet',
          reports: 'Günlük raporlar',
          transcripts: 'Döküm alıntıları',
          photos: 'Fotoğraflar',
          weather: 'Hava verisi',
        },
        generate: 'PDF dosyası oluştur',
        history: 'Dosya geçmişi',
        requested: 'Dosya istendi; hazır olunca bildirim alacaksınız.',
        download: 'İndir',
      },
      empty: 'Bu ay hava kaynaklı gecikme yok.',
      noSitesTitle: 'Henüz şantiye yok',
      noSites:
        'Bir şantiyenin raporları hava kaynaklı gecikme kaydettiğinde kayıp saatler burada görünür.',
      noWorkdays: 'Bu ayda henüz iş günü yok.',
      monthSelect: 'Ay',
      siteSelect: 'Şantiye',
      allSites: 'Tüm şantiyeler',
      previousMonth: 'Önceki ay',
      nextMonth: 'Sonraki ay',
      lostLabel: 'kayıp',
      entries: '{{count}} kayıt',
      showingOf: '{{total}} kayıttan {{shown}} tanesi gösteriliyor',
      openReport: '{{no}} raporunu aç',
      stoppedBetween: '{{start}}–{{end}} arası durdu',
      stoppedFrom: '{{start}} itibarıyla durdu',
      packDialog: {
        description:
          'Dosyada bir kapak, günlere göre gecikme özeti, her günün raporu ve nasıl onaylandığı, zaman damgalı döküm alıntıları, fotoğraflar ve ölçülen hava durumu tablosu yer alır; dosya şirketinizin rapor dilindedir.',
        from: 'Başlangıç',
        to: 'Bitiş',
        siteRequired: 'Talebin ait olduğu şantiyeyi seçin.',
        periodInvalid: 'Bitiş, başlangıçla aynı gün ya da sonrası olmalı.',
        sectionsRequired: 'En az bir bölüm seçin.',
        sitePlaceholder: 'Şantiye seçin',
        summary: '{{count}} şantiye günü · {{hours}} kayıp',
        summaryMonthOnly: 'Yalnızca {{month}}: {{count}} şantiye günü · {{hours}} kayıp',
      },
      packs: {
        empty: 'Henüz dosya yok. Gecikme kaydından bir tane oluşturun.',
        allSites: 'Tüm şantiyeler',
        requestedBy: 'İstendi: {{when}}',
        period: '{{from}} – {{to}}',
        sections: '{{count}} bölüm',
        demoDownload:
          'Demo modu: PDF’i arka plan servisi oluşturur; burada indirilecek bir şey yok.',
        notStarted:
          '10 dakika geçti, başlamadı. Şirketiniz için kanıt dosyaları şu anda oluşturulmuyor. TalkOlog destek ekibiyle iletişime geçin.',
      },
    },
    /** `PhoneInput` (W4-01): the country-code picker and the number field. */
    phone: {
      countryCode: 'Ülke kodu',
      countryButton: 'Ülke kodu: {{country}}, {{region}} +{{code}}',
      search: 'Ülke ara',
      searchPlaceholder: 'Ülke veya +kod',
      noMatch: '“{{query}}” ile eşleşen ülke yok.',
      hint: 'Uluslararası biçimde kaydedilir, ör. {{example}}',
      invalid: 'Seçilen ülke için geçerli bir cep telefonu numarası girin.',
      foreign:
        'Bu bir {{country}} numarası. TalkOlog bu numaraya {{pack}} numaranızdan mesaj gönderir.',
    },
  },
  field: {
    routes: {
      link: 'Linkiniz açılıyor…',
      today: 'Bugün',
      record: 'Kayıt',
      report: 'Rapor',
      history: 'Geçmiş raporlar',
      settings: 'Ayarlar',
      expired: 'Linkin süresi doldu',
      talk: 'Şimdi konuş',
      photos: 'Fotoğraf ekle',
      legal: 'Yasal metin',
    },
    legal: {
      version: 'Sürüm {{version}} · {{date}}',
      otherLanguage: 'Bu metin henüz sizin dilinizde yok.',
      unknown: {
        title: 'Metin bulunamadı',
        body: 'Bu metin burada yok. Ofisten en son linki isteyin.',
      },
      back: 'Geri',
    },
    notFound: {
      title: 'Sayfa bulunamadı',
      body: 'Bu sayfa yok ya da link yanlış yazılmış.',
      action: 'Bugün ekranına git',
    },
    link: {
      notFound: {
        title: 'Link bulunamadı',
        body: 'Bu link çalışmıyor. Ofisten yeni bir link isteyin.',
      },
      revoked: {
        title: 'Link artık geçerli değil',
        body: 'Bu link kapatıldı. Ofisten yeni bir link isteyin.',
      },
      disabled: {
        title: 'Hesap duraklatıldı',
        body: 'Raporlamanız duraklatıldı. Bir hata olduğunu düşünüyorsanız ofise sorun.',
      },
      none: {
        title: 'Linkinizi açın',
        body: 'TalkOlog’u mesajınızdaki linkten açın.',
      },
      failed: 'Linkiniz açılamadı. Sinyalinizi kontrol edip tekrar deneyin.',
    },
    device: {
      title: 'Bu {{name}} adlı kişinin telefonu mu?',
      body: 'Ofisin verdiği numaraya SMS veya WhatsApp ile 6 haneli bir kod gönderiyoruz; böylece buradan yalnızca siz rapor verebilirsiniz.',
      send: 'Evet, kodu gönderin',
      sending: 'Kod gönderiliyor…',
      sent: 'Kod gönderildi. 10 dakika geçerlidir.',
      codeLabel: '6 haneli kod',
      verify: 'Devam et',
      verifying: 'Kontrol ediliyor…',
      resend: 'Yeni kod gönder',
      resendIn: '{{minutes}} dakika sonra yeni kod isteyebilirsiniz',
      errors: {
        code_invalid: 'Bu kod doğru değil. Kalan deneme: {{attemptsLeft}}.',
        code_expired: 'Bu kodun süresi doldu. Yeni kod isteyin.',
        no_code: 'Önce bir kod isteyin.',
        code_undeliverable:
          'Bu numaraya SMS veya WhatsApp ile ulaşamadık. Ofisten numarayı kontrol etmesini isteyin.',
        rate_limited: 'Çok fazla deneme. {{minutes}} dakika sonra tekrar deneyin.',
        offline: 'Bağlantı yok. Sinyalinizi kontrol edip tekrar deneyin.',
        generic: 'Bir sorun oluştu. Tekrar deneyin.',
      },
    },
    welcome: {
      title: 'Merhaba {{name}} — ilk raporunuzdan önce bir dakika',
      notice:
        'Sesli notlarınız, fotoğraflarınız ve aramalarınız {{company}} için günlük raporu yazmak amacıyla kaydedilir. Ofis bunları görür; kayıtlar 2 yıl saklanır.',
      privacy: 'Aydınlatma metni',
      consentRequired: 'Raporlarımın kaydedilmesini ve işlenmesini kabul ediyorum',
      consentSms: 'Günlük rapor linkini mesajla gönderin',
      consentCall: 'Rapor saatinde beni arayın (otomatik arama)',
      consentWhatsapp: "Bana WhatsApp'tan mesaj gönderin",
      whatsappNotice:
        "Devam ederek günlük rapor linklerini ve hatırlatmaları WhatsApp'tan almayı kabul edersiniz. İstediğiniz zaman STOP yazarak durdurabilirsiniz.",
      prefLegend: 'Nasıl rapor vermek istersiniz?',
      prefLink: 'Mesajla link',
      prefLinkHint: 'Linke dokunun, anlatın, bitti. Fotoğraf ekleyin.',
      prefCall: 'Telefon araması',
      prefCallHint: 'TalkOlog sizi arar ve dinler.',
      prefNote: 'İstediğiniz zaman diğerini de kullanabilirsiniz.',
      continue: 'Devam et',
      saving: 'Kaydediliyor…',
      requiredHint: 'Devam etmek için ilk kutuyu işaretleyin.',
      failed: 'Kaydedilemedi. Sinyalinizi kontrol edip tekrar deneyin.',
    },
    welcomeTest: {
      title: '20 saniyelik bir deneme yapın',
      body: 'Bir şey söyleyin — hiçbir şey kaydedilip dosyalanmaz.',
      start: 'Denemeyi başlat',
      recording: 'Kaydediliyor',
      stop: 'Durdur',
      checking: 'Dinleniyor…',
      heard: 'Sizi net duydum: “{{text}}”',
      silent: 'Hiçbir şey duyamadım. Mikrofonu kontrol edip tekrar deneyin.',
      failed: 'Kayıt kontrol edilemedi. Yine de devam edebilirsiniz.',
      tooMany: 'Şimdilik bu kadar deneme yeter. Devam edebilirsiniz.',
      noMic: 'Mikrofon kapalı. Raporlarınızı kaydetmek için telefon ayarlarından izin verin.',
      again: 'Tekrar dene',
      skip: 'Denemeyi atla',
      continue: 'Bugüne git',
    },
    today: {
      due: 'SON SAAT {{time}}',
      changeSite: 'Şantiye değiştir',
      siteSheetTitle: 'Hangi şantiye?',
      status: {
        notSent: 'Gönderilmedi',
        inProgress: 'Hazırlanıyor',
        sent: 'Gönderildi',
        weatherDay: 'Hava günü',
        noWork: 'Bugün çalışma yok',
      },
      promptsTitle: 'İstediğiniz sırayla anlatın',
      prompts: {
        work: 'Yapılan işler ve yeri',
        crew: 'Firmalara göre ekip',
        hours: 'Çalışma saatleri',
        weather: 'Hava ve kayıp süre',
        deliveries: 'Gelen malzeme',
        safety: 'İş güvenliği',
      },
      record: 'Bugünün raporunu kaydet',
      tapToRecord: 'Kaydetmek için dokunun',
      usually: 'Genelde 2–3 dakika',
      addPhotos: 'Fotoğraf ekle',
      noWork: 'Bugün çalışma yok',
      weather: 'Hava nedeniyle kapalı',
      talkNow: 'Konuşmak mı istersiniz? Şimdi konuş',
      talkNowHint: 'Asistan tarayıcıdan dinler, numara gerekmez',
      call: 'ya da {{number}} numarasını arayın',
      dateQuestion: 'Bu rapor bugün için mi, dün için mi?',
      dateToday: 'Bugün',
      dateYesterday: 'Dün, {{date}}',
      closed: 'Bu günün raporu ulaştı. Buradan başka bir şey eklenemez.',
      weatherPhoto: 'Hava koşullarının fotoğrafını ekle',
      weatherPhotoHint: 'Hava koşullarının fotoğrafı gecikme kaydını güçlendirir.',
      daySheet: {
        noWorkTitle: 'Bugün çalışma yok mu?',
        noWorkBody: 'Ofis {{site}} için “Bugün çalışma yok” görecek.',
        weatherTitle: 'Şantiye hava nedeniyle kapalı mı?',
        weatherBody: 'Ofis {{site}} için “Hava günü” görecek.',
        since: 'Ne zamandan beri?',
        never: 'Hiç başlamadı',
        sinceHint: 'Onaylamak için ne zamandan beri olduğunu seçin.',
        workersBefore: 'Öncesinde sahada kaç kişi vardı?',
        confirm: 'Onayla',
        cancel: 'Vazgeç',
        saving: 'Kaydediliyor…',
        errors: {
          report_in_progress: 'Bugün için bir rapor zaten yolda. Önce onu bitirin.',
          report_filed: 'Bugünün raporu zaten gönderildi.',
          wrong_state: 'Bugün artık değiştirilemez.',
          date_not_allowed: 'O gün artık değiştirilemez.',
          generic: 'Kaydedilemedi. Sinyalinizi kontrol edip tekrar deneyin.',
        },
      },
      photosAdded: '{{count}} fotoğraf gönderiliyor…',
      openReport: '{{no}} raporunu aç',
      pastReports: 'Geçmiş raporlar',
      failed: 'Bugün yüklenemedi. Sinyalinizi kontrol edip tekrar deneyin.',
    },
    recording: {
      title: 'Kayıtta',
      paused: 'Duraklatıldı',
      starting: 'Başlıyor…',
      keepTalking: 'Anlatmaya devam edin — ekran açık kalır',
      pausedHint: 'Duraklatıldı — devam etmek için oynat düğmesine dokunun',
      timerLabel: 'Kayıt süresi',
      pause: 'Kaydı duraklat',
      resume: 'Kayda devam et',
      stopSend: 'Durdur ve gönder',
      addPhoto: 'Fotoğraf ekle',
      photos: 'Fotoğraflar',
      photo: 'Fotoğraf {{n}}',
      removePhoto: '{{n}} numaralı fotoğrafı kaldır',
      maxReached: '15 dakika — bu kayıt gönderiliyor. Gerekirse yeni bir kayıt yapın.',
      tooShort: 'Hiçbir şey duyamadım — tekrar deneyin',
      permission: {
        title: 'Mikrofon gerekli',
        body: 'TalkOlog’un raporunuzu duyabilmesi için mikrofona izin verin.',
        ios: 'iPhone: Ayarlar → Safari → Mikrofon → İzin ver',
        android: 'Android: adres çubuğundaki kilit simgesine dokunun → İzinler → Mikrofon',
        fallback: 'Ya da telefonunuzun ses kaydedicisiyle kaydedip dosyayı gönderin:',
        pick: 'Bir kayıt seçin',
        orCall: 'Ya da {{number}} numarasını arayın',
      },
      unsupported: {
        title: 'Bu tarayıcı burada kayıt yapamıyor',
        body: 'Telefonunuzun ses kaydedicisiyle kaydedip dosyayı gönderin.',
      },
      failed: 'Kayıt beklenmedik şekilde durdu. Tekrar deneyin.',
      interrupted: 'Mikrofon durdu — bir arama ya da başka bir uygulama mikrofonu almış olabilir.',
    },
    sending: {
      title: 'Gönderiliyor…',
      body: 'Kaydınız ofise gönderiliyor.',
      queued: 'Telefonunuza kaydedildi — sinyal gelince gönderilecek',
      queuedBody: 'Uygulamayı açık bırakın ya da sonra geri dönün; kendiliğinden gönderir.',
      failed: 'Bu kayıt gönderilemedi.',
      retry: 'Tekrar dene',
      remove: 'Bu kaydı kaldır',
      backToToday: 'Bugün ekranına dön',
      errors: {
        report_filed: 'Bugünün raporu zaten gönderildi; bu ona eklenemez.',
        day_closed: 'Bu gün bu şantiye için kapalı; bu gönderilemez.',
        date_not_allowed: 'O gün için artık rapor verilemez.',
        upload_too_large: 'Dosya çok büyük (en fazla 50 MB).',
        media_type_not_allowed: 'Bu dosya türü gönderilemez.',
        device_unverified: 'Bu telefonu doğrulamak için linkinizi yeniden açın; sonra gönderilir.',
        consent_required: 'Kurulumu bitirmek için linkinizi yeniden açın; sonra gönderilir.',
        link_expired: 'Bu linkin süresi doldu. Son mesajınızdaki linki açın; sonra gönderilir.',
        link_revoked: 'Bu link kapatıldı. Son mesajınızdaki linki açın; sonra gönderilir.',
        link_not_found: 'Bu link çalışmıyor. Son mesajınızdaki linki açın; sonra gönderilir.',
        reporter_disabled: 'Raporlamanız duraklatıldı. Ofise sorun, sonra tekrar deneyin.',
        site_inactive: 'Bu şantiye duraklatıldı. Ofise sorun, sonra tekrar deneyin.',
        file_missing: 'Dosya artık bu telefonda değil.',
        photos_window_closed: 'Bu rapora artık fotoğraf eklenemez: süre ertesi gün 23:59’da doldu.',
        photo_limit: 'Bu rapora gönderildikten sonra eklenebilecek en fazla fotoğraf eklendi.',
        generic: 'Gönderilemedi. Ofisten yardım isteyin.',
      },
    },
    processing: {
      title: 'Raporunuz yazılıyor…',
      report: 'Rapor {{no}}',
      close: 'Bunu kapatabilirsiniz — size mesaj göndereceğim',
      counts: '{{voiceNotes}} sesli not · {{photos}} fotoğraf',
      ticks: {
        transcribed: 'Yazıya döküldü',
        crew: 'Ekip bulundu',
        weather: 'Hava kaydedildi',
        hours: 'Saatler kontrol ediliyor',
      },
      tickDone: 'tamam',
      steps: {
        transcribed: 'Yazıya döküldü, şantiye gürültüsü temizlendi',
        crewOne: 'Sahada {{n}} kişi, 1 taşeron',
        crewMany: 'Sahada {{n}} kişi, {{k}} taşeron',
        weather: '{{condition}} {{start}}–{{end}} gecikme olarak kaydedildi',
        hours: 'Taşeron saatleri kontrol ediliyor…',
      },
      readyTitle: 'Raporunuz kontrole hazır',
      failed: 'Raporunuz kontrol edilemedi. Sinyalinizi kontrol edip tekrar deneyin.',
      notFound: 'Bu rapor artık burada görüntülenemiyor.',
    },
    question: {
      progress: 'Raporunuz yazılıyor',
      badge: 'Kısa kontrol',
      heardAt: 'Notunuzun {{time}} anında duyuldu',
      close: 'Bunu kapatabilirsiniz — taslak hazır olunca size mesaj göndereceğim.',
      sending: 'Cevabınız gönderiliyor…',
      workers: 'Çalışan',
      hoursEach: 'Kişi başı saat',
      hoursLost: 'Kaybedilen saat',
      hoursLegend: 'Ne kadar süre?',
      fullDay: 'Tam gün',
      setHours: 'Başka saat',
      send: 'Cevabı gönder',
      invalidNumber: 'Bir sayı girin.',
      cannotAnswer: 'Bu soru henüz buradan cevaplanamıyor. Ofis kontrol edecek.',
      other: 'Başka — söyleyin',
      otherTitle: 'Cevabınızı söyleyin',
      otherHint: 'En fazla 30 saniye. Başka bir şey kaydedilmez.',
      record: 'Cevabınızı kaydedin',
      recording: 'Kaydediliyor',
      stop: 'Durdur ve gönder',
      typeIt: 'Ya da yazın',
      noMic: 'Mikrofon kapalı. Sayıyı yazın ya da telefon ayarlarından mikrofona izin verin.',
      noMicOnly: 'Mikrofon kapalı. Telefon ayarlarından izin verin ya da bir cevap seçin.',
      cancel: 'Vazgeç',
      listening: 'Cevabınız dinleniyor…',
      retry: 'Anlayamadım. Tekrar söyleyin ya da bir cevap seçin.',
      unclear: 'Anlayamadım; ofisin kontrol etmesi için bıraktım.',
      queueFailed: 'Cevabınız bu telefonda saklanamadı. Tekrar deneyin.',
      errors: {
        question_closed: 'Bu soru zaten cevaplandı. En güncel hali bu.',
        wrong_state: 'Raporunuz değişti. En güncel hali bu.',
        answer_not_accepted: 'Bu cevap kabul edilmedi. Tekrar deneyin.',
        offline: 'Bağlantı yok. Sinyalinizi kontrol edip tekrar deneyin.',
        generic: 'Cevabınız gönderilemedi. Tekrar deneyin.',
        answer_pending: 'Önceki cevabınız hâlâ dinleniyor.',
      },
    },
    confirmation: {
      crew: '{{company}}: {{workers}} çalışan, kişi başı {{hours}} saat — toplam {{total}} saat.',
      crewOne: '{{company}}: 1 çalışan, {{hours}} saat.',
      workers: '{{company}}: {{workers}} çalışan.',
      workersOne: '{{company}}: 1 çalışan.',
      hours: '{{company}}: kişi başı {{hours}} saat.',
      companyAdded: '{{company}} ofisin onayı için eklendi.',
      companyMapped: '{{company}} olarak kaydedildi.',
      weather: 'Hava: {{hours}} saat kayıp.',
      weatherNone: 'Hava: kayıp süre yok.',
      noted: 'Anlaşıldı.',
    },
    draft: {
      eyebrow: 'Taslak · {{date}}',
      title: 'Bugünkü raporu kontrol edin',
      crew: 'Ekip',
      answered: 'Cevaplandı',
      crewValue: '{{workers}} çalışan, kişi başı {{hours}} saat',
      crewValueOne: '1 çalışan, {{hours}} saat',
      notStated: 'Belirtilmedi',
      noCrew: 'Ekip belirtilmedi',
      weather: 'Hava',
      done: 'Yapılan',
      delivered: 'Teslimat',
      safety: 'Güvenlik',
      next: 'Sonraki iş günü',
      photos: 'Fotoğraf',
      photosCount: '{{count}} eklendi',
      none: 'Yok',
      fullDayLost: 'tüm gün kayıp',
      status: {
        complete: 'tamamlandı',
        in_progress: 'devam ediyor',
        started: 'başladı',
        stopped: 'durdu',
        planned: 'planlandı',
      },
      percent: '~%{{percent}}',
      toolboxTalk: 'İş güvenliği konuşması',
      noIncidents: 'olay yok',
      incidents: '{{count}} olay',
      confirm: 'Onayla ve gönder',
      confirming: 'Gönderiliyor…',
      errors: {
        draft_changed: 'Raporunuz değişti. Tekrar kontrol edip gönderin.',
        wrong_state: 'Raporunuz değişti. En güncel hali bu.',
        offline: 'Bağlantı yok. Sinyalinizi kontrol edip tekrar deneyin.',
        generic: 'Raporunuz gönderilemedi. Tekrar deneyin.',
      },
      fix: {
        button: 'Sesle düzelt',
        title: 'Sesle düzelt',
        hint: 'Neyin değişeceğini söyleyin, örneğin “Usta Beton dört değil, beş kişiydi.” En fazla 30 saniye.',
        record: 'Düzeltmenizi kaydedin',
        recording: 'Kaydediliyor',
        stop: 'Durdur ve gönder',
        typeIt: 'Ya da yazın',
        typeLabel: 'Ne değişmeli?',
        send: 'Gönder',
        cancel: 'Vazgeç',
        noMic: 'Mikrofon kapalı. Telefon ayarlarından izin verin ya da değişikliği yazın.',
        listening: 'Düzeltmeniz dinleniyor…',
        previewTitle: 'Değişikliği kontrol edin',
        save: 'Değişikliği kaydet',
        saving: 'Kaydediliyor…',
        tryAgain: 'Tekrar dene',
        discard: 'Olduğu gibi kalsın',
        unclear: 'Bu değişikliği yapamadım.',
        unclearHint: 'Firma adı ve sayıyla tekrar söyleyin ya da sayılara dokunun.',
        stale: 'Ben dinlerken raporunuz değişti. Kontrol edip düzeltmeyi tekrar söyleyin.',
        saved: 'Değişiklik kaydedildi. Raporu tekrar kontrol edip gönderin.',
        lines: {
          workers: '{{from}} → {{to}} çalışan',
          hoursEach: 'kişi başı {{from}} → {{to}} saat',
          total: '{{from}} → {{to}} saat',
          added: '{{company}} eklendi: {{value}}',
          removed: '{{company}} çıkarıldı',
          lostHours: 'Hava: {{from}} → {{to}} saat kayıp',
          section: 'Değişti: {{section}}',
        },
        sections: {
          crew: 'Ekip',
          summary: 'Özet',
          workingHours: 'Çalışma saatleri',
          weather: 'Hava',
          work: 'Yapılan',
          deliveries: 'Teslimat',
          equipment: 'Ekipman',
          safety: 'Güvenlik',
          issues: 'Sorunlar',
          next: 'Sonraki iş günü',
        },
        errors: {
          queue: 'Kaydınız bu telefonda saklanamadı. Tekrar deneyin.',
          generic: 'Düzeltmeniz gönderilemedi. Tekrar deneyin.',
          offline: 'Bağlantı yok. Sinyalinizi kontrol edip tekrar deneyin.',
          timeout: 'Çok uzun sürdü. Tekrar deneyin ya da sayılara dokunun.',
          correction_pending: 'Son düzeltmeniz hâlâ işleniyor. Biraz bekleyin.',
        },
      },
      stepper: {
        change: '{{company}}, {{value}}: değiştir',
        hint: 'Sayıları değiştirip kaydedin.',
        workers: 'Çalışan',
        hours: 'Kişi başı saat',
        fewerWorkers: 'Bir çalışan eksik',
        moreWorkers: 'Bir çalışan fazla',
        fewerHours: 'Yarım saat eksik',
        moreHours: 'Yarım saat fazla',
        total: 'Toplam: {{total}}',
        save: 'Kaydet',
        saving: 'Kaydediliyor…',
        cancel: 'Vazgeç',
        invalid: 'Tam sayı olarak çalışan sayısı ve en fazla 24 saat girin.',
        error: 'Değişiklik kaydedilemedi. Tekrar deneyin.',
      },
    },
    filing: {
      title: 'Raporunuz dosyalanıyor…',
      body: 'Bir dakika içinde PDF bağlantısıyla bir SMS alacaksınız.',
    },
    filed: {
      title: 'Kaydedildi.',
      body: 'Günlük Rapor {{no}} ofise iletildi. Teşekkürler {{name}}, bugünlük bu kadar.',
      bodyWeather: 'Hava muhalefeti raporu {{no}} ofise iletildi. Teşekkürler {{name}}.',
      bodyNoWork: 'Çalışma yapılmayan gün raporu {{no}} ofise iletildi. Teşekkürler {{name}}.',
      pdf: 'PDF’i aç',
      pdfBadge: 'PDF',
      pdfPreparing: 'PDF hazırlanıyor…',
      pdfLate: 'PDF yolda. Raporunuz ofise ulaştı.',
      opensNewTab: '(yeni sekmede açılır)',
      addPhotos: 'Daha fazla fotoğraf ekle',
      weatherPhoto: 'Hava koşullarının fotoğrafını ekle',
      weatherPhotoHint: 'Hava koşullarının fotoğrafı gecikme kaydını güçlendirir.',
      pastReports: 'Geçmiş raporlar',
      hint: 'Sonra bir değişiklik mi gerekiyor? {{pastReports}} bölümünü açın ve neyin düzeltileceğini söyleyin. Ofis yeni bir sürüm alır; eskisi saklanır.',
      photos: {
        title: '{{no}} raporuna fotoğraf ekle',
        chosen: 'Seçilen fotoğraf: {{count}}',
        photo: 'Fotoğraf {{n}}',
        window: 'Ekleyebileceğiniz fotoğraf: {{count}}, son: {{until}}.',
        tooMany: 'En fazla {{count}} fotoğraf daha eklenebilir; ilk {{count}} fotoğraf alındı.',
        caption: 'Sesli açıklama (isteğe bağlı)',
        captionHint:
          'Fotoğrafların ne gösterdiğini söyleyin. Ofis bunu her fotoğrafın altında okur.',
        record: 'Açıklama kaydet',
        stop: 'Durdur',
        recording: 'Kaydediliyor',
        captionReady: 'Açıklama kaydedildi',
        removeCaption: 'Açıklamayı kaldır',
        noMic: 'Mikrofon kullanılamıyor. Fotoğraflar açıklamasız gönderilecek.',
        send: 'Fotoğrafları gönder',
        cancel: 'Vazgeç',
        adding: 'Fotoğraflarınız ekleniyor…',
        captioning: 'Fotoğraflarınız ve açıklamanız ekleniyor…',
        late: 'Fotoğraflarınız yolda. Ofis onları açıklamanızla birlikte alacak.',
        added: 'Fotoğraflar eklendi. Sürüm {{version}} ofiste.',
        failed: 'Fotoğraflar eklenemedi.',
      },
    },
    talk: {
      title: 'Şimdi konuş',
      assistant: 'TalkOlog asistanıyla konuşuyorsunuz',
      status: {
        microphone: 'Mikrofon izni isteniyor…',
        connecting: 'Asistana bağlanılıyor…',
        reconnecting: 'Bağlantı koptu — yeniden bağlanılıyor…',
        speaking: 'Asistan konuşuyor',
        listening: 'Asistan sizi dinliyor',
        muted: 'Mikrofonunuz kapalı',
        ended: 'Görüşme bitti. Raporunuza bakılıyor…',
      },
      timerLabel: 'Görüşme süresi',
      noticeLabel: 'Bu görüşme kaydediliyor',
      mute: 'Mikrofonu kapat',
      unmute: 'Mikrofonu aç',
      endCall: 'Görüşmeyi bitir',
      enableAudio: 'Asistanı duymak için dokunun',
      recordInstead: 'Kayıtla devam et',
      errors: {
        rate_limited:
          'Bu saat içinde üç görüşme başlattınız. {{minutes}} dk sonra yeniden arayabilirsiniz ya da raporunuzu kaydedin.',
        talk_now_unavailable: 'Şimdi konuş şu anda kullanılamıyor. Raporunuzu kaydedin.',
        voice_unavailable: 'Asistana ulaşılamadı. Yeniden deneyin ya da raporunuzu kaydedin.',
        kind_not_allowed: 'Bu link yalnızca fotoğraf içindir.',
        connect: 'Görüşme bağlanamadı. Sinyalinizi kontrol edip yeniden deneyin.',
        generic: 'Görüşme başlatılamadı. Sinyalinizi kontrol edip yeniden deneyin.',
      },
    },
    photosLink: {
      title: 'Fotoğraf ekle',
      intro: '{{no}} numaralı rapor için fotoğraflar, {{site}}, {{date}}.',
      pick: 'Fotoğraf çek ya da seç',
      noReport:
        'Bu gün için henüz gönderilmiş bir rapor yok. Rapor gönderilince fotoğraf eklenebilir.',
      closed: 'Bu linkle artık fotoğraf eklenemez.',
      failed: 'Rapor yüklenemedi. Sinyalinizi kontrol edip yeniden deneyin.',
    },
    offline: {
      banner: 'Sinyal yok — kayıtlar telefonunuza kaydediliyor',
      queued: 'Gönderilmeyi bekleyen: {{count}}',
      failed: 'Gönderilemedi: {{count}}',
      review: 'İncele',
      sheetTitle: 'Gönderilemeyenler',
      voiceNote: 'Sesli not · {{date}}',
      photo: 'Fotoğraf · {{date}}',
      remove: 'Kaldır',
      unsaved: 'Bu telefona kaydedilemedi: gönderilene kadar TalkOlog’u açık tutun.',
    },
    history: {
      intro: 'Son 14 gündeki raporlarınız.',
      empty: 'Son 14 günde rapor yok.',
      status: {
        collecting: 'Devam ediyor',
        processing: 'Devam ediyor',
        awaiting_answer: 'Cevabınızı bekliyor',
        awaiting_confirmation: 'Kontrol için hazır',
        filed: 'Gönderildi',
        signed_off: 'Onaylandı',
        void: 'İptal edildi',
      },
      late: 'Geç',
      eyebrow: 'Rapor · {{date}}',
      version: 'Sürüm {{version}}',
      continue: 'Bu rapora devam et',
      noData: 'Henüz gösterilecek bir şey yok: TalkOlog bu raporu hâlâ yazıyor.',
      back: 'Geçmiş raporlara dön',
      failed: 'Geçmiş raporlarınız yüklenemedi. Sinyalinizi kontrol edip tekrar deneyin.',
      reportFailed: 'Bu rapor yüklenemedi. Sinyalinizi kontrol edip tekrar deneyin.',
      change: {
        button: 'Bir şeyi değiştir',
        title: 'Bir şeyi değiştir',
        hint: 'Neyin değişeceğini söyleyin, örneğin “Apex’te altı değil yedi kişi vardı.” En fazla 30 saniye.',
        lastChange: 'Son değişiklik: {{note}}',
        saved: 'Değişiklik kaydedildi. Sürüm {{version}} ofiste.',
        stale: 'Ben dinlerken rapor değişti. Kontrol edin, sonra değişikliği yeniden söyleyin.',
        locked: 'Bu rapor kilitli. Değiştirmesi için ofise başvurun.',
      },
    },
    expired: {
      title: 'Bu linkin süresi doldu',
      body: 'Bugünün linkini, ofisin sizin için kayıtlı telefon numarasına mesajla gönderebiliriz.',
      noCode: 'Son mesajınızdaki linki açın ya da ofisten bugünün linkini isteyin.',
      send: 'Bugünün linkini gönder',
      sending: 'Gönderiliyor…',
      sent: 'Gönderildi. Size az önce gönderdiğimiz mesajdaki bugünün linkini açın.',
      reasons: {
        site_paused: 'Bu şantiye TalkOlog’da duraklatıldı. Ofisten yeniden açmasını isteyin.',
        site_inactive: 'Bu şantiye artık TalkOlog’daki listenizde değil. Ofise sorun.',
        not_working_day: 'Bugün bu şantiyede çalışma günü değil; bugün için link yok.',
        no_consent: 'Mesajları kapattınız; linki size mesajla gönderemiyoruz. Ofisten isteyin.',
        opted_out: 'Size mesaj göndermememizi istediniz; linki gönderemiyoruz. Ofise sorun.',
        no_channel: 'Numaranıza mesaj gönderemedik. Ofisten numaranızı kontrol etmesini isteyin.',
      },
      errors: {
        rate_limited: 'Birkaç kez istediniz. {{minutes}} dk sonra tekrar deneyin.',
        offline: 'Bağlantı yok. Sinyalinizi kontrol edip tekrar deneyin.',
        generic: 'Link gönderilemedi. Tekrar deneyin.',
      },
    },
    settings: {
      channelTitle: 'Mesajlarınızı nereye gönderelim?',
      channelDefault: 'En uygun kanal',
      channelDefaultHint: 'TalkOlog şirketinizin sırasına göre seçer',
      channels: {
        sms: 'Kısa mesaj (SMS)',
        whatsapp: 'WhatsApp',
        push: 'Bu telefonda bildirim',
      },
      consentsTitle: 'Verdiğiniz onaylar',
      legalTitle: 'Gizlilik ve kayıt',
      consents: {
        recording: 'Sesli notlarımın, fotoğraflarımın ve aramalarımın kaydedilmesi',
        data_processing: 'Raporlarımın {{company}} için işlenmesi',
        sms: 'Günlük rapor linkimin mesajla gönderilmesi',
        auto_call: 'Rapor saatinde otomatik arama',
        push: 'Bu telefonda bildirim',
        whatsapp: "Günlük rapor linkimin WhatsApp'tan gönderilmesi",
      },
      push: {
        title: 'Bildirimler',
        label: 'Bu telefonda bildirim',
        hint: 'Rapor hatırlatmalarınız bildirim olarak gelir. Bu telefona ulaşamazsa size mesaj göndeririz.',
        blocked:
          'TalkOlog bildirimleri bu telefonda engelli. Telefonunuzun ayarlarından izin verin, sonra buradan açın.',
        failed: 'Bildirimler açılamadı. Tekrar deneyin.',
      },
      on: 'Açık',
      off: 'Kapalı',
      turnOff: 'Kapat',
      turnOffLabel: 'Kapat: {{consent}}',
      stopSheet: {
        title: 'Rapor vermeyi durdurmak mı istiyorsunuz?',
        body: 'Bu kapalıyken TalkOlog sesli notlarınızı ve fotoğraflarınızı alamaz, raporlarınız durur. Ofise haber verilir. Yeniden rapor vermek için bu telefonda tekrar onay verirsiniz.',
        confirm: 'Kapat',
        cancel: 'Açık kalsın',
      },
      turnOn: 'Aç',
      turnOnLabel: 'Aç: {{consent}}',
      turnOnSheet: {
        title: 'Mesajlar yeniden açılsın mı?',
        titleWhatsapp: 'WhatsApp mesajları açılsın mı?',
        titleCall: 'Otomatik aramalar açılsın mı?',
        body: 'Açarak şunu yeniden kabul etmiş olursunuz:',
        note: 'İstediğiniz zaman buradan kapatabilirsiniz.',
        confirm: 'Aç',
        cancel: 'Şimdi değil',
      },
      textsStopped: {
        start:
          'STOP yazarak mesajlarımızı durdurdunuz. Yeniden almak için mesajlarımızdan birine START yazın.',
        office: 'Bu telefona mesaj gönderimi durduruldu. Ofise danışın.',
      },
      notToday: {
        title: 'Bugünkü aramalar',
        label: 'Bugün beni aramayın',
        hint: 'Bugünkü hatırlatmalar mesaj olarak gelir. Aramalar yarın yeniden başlar.',
        button: 'Bugün beni aramayın',
        on: 'Bugün başka arama yok',
        onHint: 'Bugünkü hatırlatmalar mesaj olarak gelir. Aramalar yarın yeniden başlar.',
      },
      signOut: 'Bu telefonda oturumu kapat',
      signOutSheet: {
        title: 'Bu telefonda oturum kapatılsın mı?',
        body: 'Bu telefondan yeniden rapor vermek için linkinizi açın ve size mesajla gönderdiğimiz yeni kodu girin.',
        confirm: 'Oturumu kapat',
        cancel: 'Vazgeç',
      },
      saving: 'Kaydediliyor…',
      saved: 'Kaydedildi',
      back: 'Bugün’e dön',
      failed: 'Ayarlarınız yüklenemedi. Sinyalinizi kontrol edip tekrar deneyin.',
      saveFailed: 'Kaydedilemedi. Sinyalinizi kontrol edip tekrar deneyin.',
    },
  },
};
